import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Common/Header';
import CompanyModal from '../components/Companies/CompanyModal';
import ScreenshotCapture from '../components/Products/ScreenshotCapture';
// 需求 R152（PM 2026-09-01）：產品縮圖 hover 下載圖片小圖示。
// 下載行為與產品詳情頁共用 utils/productImageDownloader.js，見該元件檔頭。
import ProductImageDownloadButton from '../components/Products/ProductImageDownloadButton';
import { usePermission } from '../utils/usePermission';
import { sortQuoteItems } from '../utils/productSort';
// 需求 R121 R2：保存期限取值（快照 → populate 產品 → 實時產品）與匯出共用同一支。
import { getItemShelfLife } from '../utils/quoteShelfLife';
// 需求 R136（PM 2026-08-24 拍板 Q1=B／Q5）：重複品項「照樣加入、只在搜尋結果列事前標示」。
import { DUPLICATE_ITEM_HINT, buildQuotedProductIdSet, isProductInQuote } from '../utils/quoteDuplicateItems';
import {
  getGlobalRate,
  getRateDecimals,
  isRateInputAllowed,
  validateCustomRate,
} from '../utils/customExchangeRate';
// 需求 R106 R6：選品區系列／類別的多選下拉改用與型錄管理相同的呈現（樣式單一來源）。
import '../styles/multiSelectDropdown.css';
// 需求 R123：Section 2 產品選取第二列新增「建立時間區間」欄位（沿用型錄新品目錄的 class 與樣式）。
import '../styles/dateRangeFilter.css';
import { validateProductDateRange } from '../utils/dateRange';
// 需求 R132（PM 2026-08-27）：單價算式收斂成單一共用模組（AC8）。
// 本頁與 QuoteEditPage.js 過去各留一份複製品，已漂移過不只一次；現在兩頁 import 同一個實作。
// 同時修掉 PM 回報的「1 階 61.0 應為 60.9、4 階 66.2 應為 66.1」：
// ‧ 根因 A 標準成本被無條件進位兩次 → 成本基準改讀「箱價 ÷ 箱入數」未進位原值。
// ‧ 根因 B roundPrice 缺浮點雜訊防線 → 改走 quoteMoney.js 的 ceilMoney。
// 需求 R70（成本在產品管理的顯示與儲存維持進位到小數 1 位）不變，詳見 utils/quoteUnitPrice.js 檔頭。
import {
  normalizeCostSource,
  resolveEntryBaseCost,
  getBaseCostFromSource,
  // 需求 R170（PM 2026-09-15 Q1=A／Q3=A）：加入產品與抽屜切換來源的 MOQ 都改由此 helper 取
  // 「使用此成本報價」勾選來源的成本項，不再寫死 factoryCost（AC8 兩處共用、不各自 inline）。
  getCostEntryFromSource,
  getEffectiveBaseCost,
  formatBaseCost,
  formatPrice,
  calcUnitPrice,
  // 需求 R145：冷凍系列判定的唯一真實來源同樣在 quoteUnitPrice.js（Q6=A 快照優先的三層 fallback）。
  resolveItemSeries,
  // 需求 R149：自訂箱成本 →（÷ 箱入數）→ 包成本。換算式只存在於 quoteUnitPrice.js，
  // 本頁只負責 UI 與 state，絕不在這裡自己除一次（AC8）。
  packCostFromCustomBoxCost,
  canUseCustomBoxCost,
  CUSTOM_BOX_COST_NO_PCS_HINT,
} from '../utils/quoteUnitPrice';
// 需求 R151（PM 2026-08-31 Q4=A）：TIER_MAP／getDefaultPriceTier 原本是本頁與編輯頁各一份的
// page-local 函式；產品詳細頁「歷史報價」的「!」也要用同一套規則算「價格階是否非預設」，
// 再複製第三份就會各自演化 → 抽到 utils/quotePriceTier.js，三處 import 同一份（AC6）。
import { getDefaultPriceTier } from '../utils/quotePriceTier';
import './QuoteCreatePage.css';

const API = process.env.REACT_APP_API_URL || '/api';

// 需求 R103（PM 2026-08-03 留言「還有問題待釐清先不實作」）：
// 本常數即將由「客戶分級 → 階數」單維改為「客戶分級 × 成本來源 → 階數」雙維。
// 新對照表（尚未套用，PM 未拍 Q1 前不動任何一行程式）：
// 分級   商品選「工廠」   商品選「經銷（任一）」
// A        2 階              1 階
// B        3 階              2 階
// C        4 階              3 階
// D        4 階              4 階
// 「經銷（任一）」＝ distributor_0 / distributor_1 同階；legacy 'distributor' 需先經
// normalizeCostSource（本檔 :223）正規化。淨效果：經銷來源階數與現行完全相同，
// 工廠來源一律升一階（D 級已達 4 階上限故不變）。
// 邊界：只換對照表，calcTierPrice / calcUnitPrice / applySpecialFormula / roundPrice
// 一行都不改，以免動到 需求 R70 / 160 / 161 已拍板的運算順序與進位。
//
// PM 2026-08-03 已拍板：
// Q2=A 客戶分級為空 / 未選客戶 → 維持現行 1 階（不比照 A 級分來源）。
// Q3=B 已儲存的既有報價單，開啟編輯頁時「用新規則重算 priceTier」（歷史單開啟即變價）。
// Q4=A 啟用自訂成本（customCostEnabled）的品項，階數仍依原本勾選的 costSource 判定。
// Q5=A 編輯頁（QuoteEditPage）一併補齊 defaultPriceTier 紅點機制。
// Q6=B 畫面不顯示「本列預設階數的由來」tooltip。
// Q7=A 上線即生效。
//
// Q1 仍待釐清（PM 第二次追問：「切成經銷1 後那都已經並非原本的預設(工廠2階)了，
// 無論如何紅點都會存在，似乎與你的 A/C 說明有落差，請重新解釋」）：
// ── 先更正上一輪的用詞（本檔第一版註解把這裡講得太粗）──
// PM 說「無論如何紅點都會存在」在該情境下是對的，但理由不是階數：
// 紅點只有一顆（本檔 :1958-1971 的 .price-modified-dot），觸發條件是四個 OR，
// title 把成立的理由逐行列出（:1963-1968）：
// isManualPrice        → 「單價已手動調整，點擊還原」
// isCustomCostActive   → 「已使用自定成本換算」
// isCostSourceChanged  → 「成本來源已變更」（:950，newSource ≠ 產品本身的 costSource）
// isPriceTierChanged   → 「價格階已調整為非預設（預設為 N 階）」（:1740）
// 情境裡使用者「把成本來源從工廠切成經銷1」這個動作本身就讓 isCostSourceChanged=true，
// 所以 A、C 兩案的紅點都會亮著。A/C 的差別不在「紅點在不在」，而在
// 「紅點 title 裡還有沒有階數那一行」＋「階數值與單價有沒有被改寫」。
//
// ── 關鍵前提：defaultPriceTier 是「活基準」，不是加入商品當下的凍結快照 ──
// 它會被同步刷新：換客戶時 :550-566 就把每列 defaultPriceTier 重刷成新客戶的預設值。
// 所以「預設」永遠指「以當下的規則（客戶分級 × 本列成本來源）算出來的值」，
// 不是「這列最初加入時是工廠 2 階」。PM 的直覺是後者，這正是落差來源。
//
// ── 同一情境逐項對照（A 級客戶、該列原為工廠＝新規則預設 2 階、
// 使用者手動改成 4 階、再把成本來源切成經銷1＝新規則預設 1 階）──
// A（一律連動）：priceTier 4 → 被系統改寫成 1；defaultPriceTier 也變 1
// → isPriceTierChanged=false → 紅點仍亮，但 title 只剩「成本來源已變更」，
// 「價格階已調整為非預設」那一行消失；單價的標準成本部分 ÷0.92 → ÷1
// （約 -8%）。使用者手動選的 4 階無聲被清掉，只能自己再改回去。
// C（連動但跳過手動列）：priceTier 維持 4；defaultPriceTier 更新為 1
// → isPriceTierChanged=true → 紅點亮，title 兩行都在：「成本來源已變更」
// ＋「價格階已調整為非預設（預設為 1 階）」；單價完全不變。
// 沒手動改過階數的列，A 與 C 行為完全相同（都跟著成本來源連動，紅點也不會因階數而亮）。
// 若情境改成「不切成本來源、只是換了客戶分級」，A 案下就真的整顆紅點都不見
// （前提是另外三個原因都不成立），C 案則會續亮階數那一行。
//
// C 的代價（實作差異）：需要一個「本列階數曾被手動改過」的旗標。目前 items 沒有這個欄位，
// 且後端 Quote schema（src/server/models/Quote.js:15）只存 priceTier，沒有對應欄位可存，
// 故 C 若要在「儲存 → 重新開啟編輯頁」後仍生效，必須替 quoteItemSchema 新增布林欄位
// （比照既有的 isManualPrice / isCostSourceChanged）；否則重開單後 C 會退化成 A。
// 另注意 Q3=B 與 Q1 交互：既有單開啟即重算，等於「儲存過的手動階數」在重開時也會被
// 新規則覆寫；若 PM 要保住手動值，Q1 必須選 C 且一併加上述旗標（見任務評論 Q8）。
//
// Q9 新增待拍板（由 PM 這次追問直接長出來的第三種語意，本輪未實作）：
// 「預設」要用哪個時間點？
// Q9-A（現況／建議）：活基準 —— defaultPriceTier 隨「客戶分級 × 本列成本來源」即時重算，
// 階數紅點的意思是「這列現在的值 ≠ 現在規則算出的值」。
// Q9-B（PM 直覺的那個）：凍結快照 —— defaultPriceTier 記住「加入商品當下」的預設（工廠 2 階），
// 之後不論換客戶或切來源都不更新。這樣上述情境即使 A 案也會續亮
// 「預設為 2 階」，但副作用是：沒被手動改過、值就等於新規則預設的列，
// 也會因為基準停在舊值而被標成「非預設」（誤報）。
// Q9 與 Q1 正交：Q1 決定「階數值要不要被改寫」，Q9 決定「拿什麼當比較基準」。
//
// ── PM 2026-08-03 第三次追問：「新對照表尚未上線的『現況』下，客戶自動帶 2 階、
// 使用者手動改成 1 階、再去抽屜把成本來源從工廠改成經銷1，此時單價如何換算？」
// （PM 明示「只是查找現況、並非修改」→ 以下純現況記錄，本輪未改任何計價行為）
//
// 結論一句話：階數「不會」被動到，維持使用者手動選的 1 階；系統只把「標準成本」
// 換成經銷1 的單價，然後把同一條算式整條重跑一次。
//
// 逐步（本檔實際程式路徑）：
// 1. 手動改階數 → updateItem(:840-859)：priceTier 落到 item 上，因 priceTier 在
// 重算白名單內且 isManualPrice=false → 立刻用新階數重算 unitPrice。
// 注意此處沒有留下任何「使用者手動改過階數」的旗標（正是 Q1=C 缺的那個欄位）。
// 以下第 2 點是 2026-08-03 當下的現況記錄，需求 R104（Q1=A）已改為連動，僅存查。
// 2. 抽屜切成本來源 → handleCostSourceChange(:941-963)：整個函式沒有任何一行寫
// item.priceTier —— 這就是「現況＝成本來源與階數不連動」的證據。它只做四件事：
// costSource=新值；baseCost=getEffectiveBaseCost(:949)；
// isCostSourceChanged=（新來源 ≠ 產品主檔 costSource，:950）；MOQ 換成新來源的（:955）。
// 3. baseCost 來源：getBaseCostFromSource(:227-236) 對 distributor_1 取
// distributorCosts[1] 的 unitPrice（缺值時 boxPrice÷箱入數，:210-214）。
// 若該列有勾自訂成本且值為有限正數，仍以自訂成本覆寫（:240-247）。
// 4. 重算 → calcUnitPrice(:272-320)，其中階數取 item.priceTier || 1（:300）＝手動的 1 階。
// calcTierPrice(:155-164) 1 階＝ baseCost ÷ 1（不打折）+ 16×材積÷箱入數。
//
// 現況單價公式（無特殊公式時）：
// 單價 = roundPrice( ( 經銷1標準成本 + 16×材積÷箱入數 ) ÷ 匯率
// × (1 + 全單加成%) + 單品加價 + 貼標費 )
// roundPrice(:184-189)＝TWD 進位到小數 1 位、USD/EUR 進位到小數 2 位，只在末端進位一次。
// 數例（經銷1 成本 100、材積 0.5、箱入 12、TWD、無加成/貼標）：
// volCost = 16×0.5/12 = 0.667；手動 1 階 → 100 + 0.667 = 100.667 → 100.7。
// 同一列若沒手動改、留在客戶帶出的 2 階 → 100÷0.975 + 0.667 = 103.231 → 103.3。
// 兩個例外（與階數無關、現況已如此）：
// ‧ 特殊公式有值 → 走 hasFormula 分支(:287-295)，階數整個失效（需求 R82 D1）。
// ‧ 該列單價曾被手動輸入（isManualPrice）→ :957 直接跳過重算，單價原封不動。
//
// 畫面提示（同一情境，現況）：紅點會亮，title 兩行都在——
// 「成本來源已變更」（isCostSourceChanged=true）
// ＋「價格階已調整為非預設（預設為 2 階）」（priceTier=1 ≠ defaultPriceTier=2，:1740）。
// 因為 handleCostSourceChange 不動 defaultPriceTier，它仍是加入商品時寫入的 2（:770/:797），
// 只有「換客戶」的 useEffect(:550-566) 會重刷它。
//
// 對 Q1 的意義：現況等於「不連動」。所以 Q1 的 A（一律連動）與 C（連動但跳過手動列）
// 兩案都是新行為；若 PM 希望「維持現在這樣」，那是第三個選項（Q1=B 不連動，
// 只有加入商品與換客戶時才決定階數），此選項下上述單價與紅點行為完全不變。
// 後續：PM 先在 需求 R103 選 B，再於 需求 R104（2026-08-04）改選 A（一律連動）。
// 以上整段「不連動」的敘述僅為當時的現況存查，實際行為以本檔 需求 R104 段落為準。
//
// ── Q3=B（歷史單開啟即重算）落地前的五項查證（2026-08-03 本輪 Grep/Read 實測）──
// F1 客戶分級取得得到：GET /quotes/:id 的 populate 有帶 level
// （src/server/controllers/quoteController.js:100 `.populate('customer','name level contacts')`），
// 所以編輯頁載入當下即可算新階。但 setSelectedCustomer 只在 q.customer 是 populated
// 物件時才設（QuoteEditPage.js:386-388）；若該單根本沒綁客戶（customer=null、只有
// customerName 純文字），分級不可得 → 依 Q2=A 整單落 1 階。→ 見 Q10-a。
// F2 歷史單的 costSource 一律讀成 'factory'：quoteItemSchema.costSource 預設值就是
// 'factory'（src/server/models/Quote.js:16），早於此欄位的舊單品項沒有存值，讀回會
// 套用預設 → 在新對照表下必定被判「工廠」而升一階，即使當初其實是照經銷成本報的價。
// F3 Q3=B 只重算「畫面」不寫回 DB：重算結果落在編輯頁 state，使用者沒按儲存就離開時
// DB 不變 → 同一張單在詳情頁（QuoteDetailPage.js:448 直接顯示 DB 的 priceTier）與
// PDF / Excel 匯出仍是舊價，編輯頁卻是新價。→ 見 Q10-b。
// F4 已審核單也會被重算：編輯頁允許 approved 進入但整頁唯讀（QuoteEditPage.js:277
// `isLocked = quoteStatus === 'approved'`），Q3=B 照字面執行會把已核准單也重算成新價
// 且無法存回 → 畫面價 ≠ 已核准價。建議 Q3=B 排除 isLocked 單。→ 見 Q10-c。
// F5 兩頁 getBaseCostFromSource 的差異不只 normalizeCostSource：建立頁（本檔 :227-236）
// 走 resolveEntryUnitPrice，缺 unitPrice 時用 箱價÷箱入數 補算（:210-214）；
// 編輯頁（QuoteEditPage.js:113-121）是直接 `?.unitPrice ?? null`，既沒正規化也沒補算
// → legacy 'distributor' 或「只有箱價沒單價」的成本項，在編輯頁切成本來源會把
// baseCost 打成 null。此為本票之前既有的缺口，AC4 補正規化時要連補算一起補齊。
//
// ── 2026-08-03 實作範圍（票由「待排期」移至「待處理」＝授權實作）──
// 已實作：雙維對照表（本常數）＋ getDefaultPriceTier()、加入商品逐列判定、換客戶時逐列
// 重刷 defaultPriceTier、紅點基準的缺值回推；編輯頁另補 normalizeCostSource、
// Q3=B 載入重算、Q5=A 紅點。
//
// ── PM 2026-08-03 第四次留言，Q8 / Q9 拍板（本輪落地）──
// 原文：「Q8：手動調過的階數重開編輯頁是否保住，是。
// Q9：紅色驚嘆號是指又手動調整（非預設）就會出現。」
// Q8=A（保住）→ 需要一個「本列階數被人手改過」的旗標：
// ‧ 後端 src/server/models/Quote.js 新增 isManualPriceTier，兩頁存檔 payload 一併送。
// ‧ 編輯頁載入既有單時，Q3=B 的重算只作用在「非手動」的列；手動列保住存檔階數。
// ‧ 已知邊界：上線前的舊單沒有旗標（讀回為 default false），「上線前手動調過的階數」
// 無從辨識，仍會被 Q3=B 重算一次。已在需求討論中標明。
// Q1 當時為 B（PM 第三次留言「Q1 已回覆，不連動」）—— 已於 需求 R104 被推翻，見下段。
// Q9 = 紅點以「手動調整（非預設）」為語意。實作上維持既有的
// isPriceTierChanged = priceTier ≠ defaultPriceTier，並由 applyDefaultTier() 撐住
// 不變式「isManualPriceTier 為真 ⇔ 本列階數非預設」，兩者因此等價。
// 為何不直接把紅點寫成 !!isManualPriceTier：AC9 的兩個保護（沒綁客戶 / 已審核）
// 規定 defaultPriceTier 直接對齊存檔值以免整排誤報，此時手動列的旗標仍須留 true
// （否則使用者補選客戶後，Q8 要保住的階數會被當成非手動而被規則覆寫）；
// 若紅點綁旗標，這兩種情況會亮出「預設為 N 階」卻剛好等於現值的自相矛盾提示。
// 綁值不會有這個問題，且在使用者操作得到的所有路徑上與綁旗標同義。
//
// ── 需求 R104：PM 2026-08-04 拍板 Q1=A / Q2=A，推翻 需求 R103 的 Q1=B ──
// 原文：「Q1：A. 一律覆寫成新來源的預設階。但一但『重新選擇』意指非預設，則啟動驚嘆號。
// Q2：A. MOQ 清空（顯示為空，忠實對應新來源）。
// Q3 / Q4 / Q5：OK。」
// ⇒ 本檔上方 需求 R103 那幾段「切成本來源不連動階數」的敘述自此作廢（已改寫），
// handleCostSourceChange 現在會呼叫 applyDefaultTier(item, level, undefined, true)：
// ‧ Q1=A：階數一律重算為「客戶分級 × 新成本來源」的預設值，連手動列（Q8=A 保住的
// 那種）也覆寫、isManualPriceTier 歸零 → 切完來源不會亮「價格階已調整為非預設」。
// ‧ PM 補述「重新選擇意指非預設 → 啟動驚嘆號」＝覆寫後使用者若再自己挑一個非預設
// 階數，旗標與紅點才重新成立（那是 updateItem 的 nextTier 路徑，行為未變）。
// ‧ Q2=A：MOQ 一律換成新來源的值，新來源沒填就清空（不再沿用舊來源）。
// ‧ Q3 / Q4 / Q5=OK：不批次改寫既有單（只在使用者實際切換的當下生效）；未綁客戶／
// 已審核單維持現行保護（不重算階數）；isManualPrice 的列單價仍不重算。
// tests/quotePriceTierCostSource.fixture.js 的 Q1 靜態斷言已同步反轉為「必須連動」。
// 需求 R151：TIER_MAP 與 getDefaultPriceTier 的本體已移到 utils/quotePriceTier.js
// （本頁 import 進來，見檔頭 import 區）。值與行為一字未改，只是把「兩頁各一份」
// 收斂成「三處共用一份」——產品詳細頁「歷史報價」的「!」是第三個使用者。

// 需求 R103（PM 2026-08-03 Q8=是 / Q9）：把「當下規則預設階」套回單一列，維持不變式
// 「isManualPriceTier 為真 ⇔ 本列階數非預設」。三個呼叫點共用（換客戶、切成本來源、
// updateItem 手動改階數）；編輯頁載入既有單另有一段獨立處理。
// ‧ 非手動列：priceTier 直接跟著規則走 → 不會留下與規則脫節、卻因為沒旗標而不警示的列。
// ‧ 手動列（Q8=是）：保住使用者選的階數，只更新比較基準 defaultPriceTier；
// 若手動值剛好等於新預設，旗標歸零（依 Q9 語意紅點應熄滅）。
// nextTier == null（換客戶 / 切成本來源）走「非手動列跟規則」分支；
// 傳入 nextTier（updateItem）視為使用者主動選階，旗標置 true（除非選回預設值）。
// 回傳新物件；呼叫端若發現 priceTier 有變，要重跑 recalcItem 讓單價跟上。
// 需求 R104（PM 2026-08-04 Q1=A）：新增第四個參數 forceDefault ——「一律覆寫成新來源的預設階」。
// 只有 handleCostSourceChange 會傳 true：使用者在抽屜親手換了成本來源，PM 要求該列的階數
// 無條件回到「客戶分級 × 新來源」的預設值，連手動列（Q8=A 保住的那種）也一起覆寫、旗標歸零。
// PM 原文附帶條件「但一旦『重新選擇』意指非預設，則啟動驚嘆號」＝覆寫後若使用者再去階數欄
// 手動挑一個非預設值，紅點才重新亮起（那條路徑走 nextTier 分支，行為不變）。
// forceDefault 不破壞不變式：isManual 直接為 false，而 priceTier 同時被設成 newDefault。
function applyDefaultTier(item, level, nextTier, forceDefault) {
  const newDefault = getDefaultPriceTier(level, item.costSource);
  const isUserPick = nextTier != null;
  const tier = isUserPick ? nextTier : item.priceTier;
  const isManual = !forceDefault
    && (isUserPick ? true : !!item.isManualPriceTier)
    && Number(tier) !== Number(newDefault);
  return {
    ...item,
    defaultPriceTier: newDefault,
    priceTier: isManual ? tier : newDefault,
    isManualPriceTier: isManual,
  };
}

// 需求 R73：選擇商品搜尋每頁筆數（配合後端 getAll 的 page/limit 分頁）
const PRODUCT_PAGE_SIZE = 50;

// 需求 R132（PM 2026-08-27）：calcTierPrice / applySpecialFormula / roundPrice / formatPrice /
// resolveEntryBaseCost / normalizeCostSource / getBaseCostFromSource / getEffectiveBaseCost /
// calcUnitPrice 全數移到 src/client/src/utils/quoteUnitPrice.js，本頁與編輯頁 import 同一份（AC8）。
// ‧ 舊版 resolveEntryUnitPrice「unitPrice 優先、缺值才用箱價÷箱入數」已反轉為
// resolveEntryBaseCost「箱價÷箱入數（未進位原值）優先、缺箱價才回落 unitPrice」——
// 這就是 PM 回報「1 階 61.0 應為 60.9／4 階 66.2 應為 66.1」的根因修正。
// ‧ 舊版 roundPrice 的裸 Math.ceil 已改走 quoteMoney.js ceilMoney（含浮點雜訊防線）。
// 任何要調整單價算式的人請直接改 utils/quoteUnitPrice.js，不要在本頁再開一份。

// 需求 R82（PM 2026-07-22 拍板 D1~D4）：報價單單價運算重定義。
// 本單重新定義「特殊公式」欄位的行為，與過往描述方向不同，以本單為準。
// - 情境一 特殊公式「有值」：「價格階數」直接失效不套用；單價 = 標準成本 →（套用特殊公式）
// → 全單加成 → 單品加價 → 貼標費用。
// - 情境二 特殊公式「為空」：維持階數運算；單價 = 標準成本 → 階數 → 全單加成 → 單品加價 → 貼標費用。
// PM 拍板決策：
// - D1 材積成本：特殊公式生效、階數停用時「純以標準成本進特殊公式」，不加材積成本(16×材積/箱入數)。
// - D2 加成順序：確定改為「全單加成 → 單品加價」（先套百分比乘法，再加單品加價）。
// - D3 幣別匯率（需求 R83 PM 2026-07-24 缺陷修正——換算「時機」）：非 TWD 幣別時，只在
// 「TWD 成本基礎」全部算完後換算一次。 修正 需求 R82「先 ÷匯率 再算階數」的缺陷：
// 材積成本 16×材積/箱入數 屬 TWD 計價，若換算置於階數之前，材積成本會被直接加到目標幣別
// 單價上而未一起換算，導致換算後單價異常偏高（PM 案例：USD 30.8、成本 80、材積 1.19，
// 預期 (80+1.19)/30.8=2.636→2.64，錯誤算成 80/30.8+1.19=3.79）。
// ‧ 情境二（階數/材積）：在 TWD 上算完標準成本+階數+材積 → 再 ÷匯率 一次。
// ‧ 情境一（特殊公式）：維持先 ÷匯率 再套公式（公式為非匯率純係數、順序不影響結果）。
// (a) 匯率方向正確且「動態跟隨輸入」：rate 取自後端 /exchange-rates 的 hkd_quote/usd_quote/
// eur_quote（getCurrentRate），並非寫死常數；匯率數值變動時 useEffect（依賴 exchangeRates）
// 會重算所有品項單價，故報價會跟著當次設定的匯率調整（PM 2026-07-24 確認：匯率非固定值）。
// (b) 特殊公式「不會」造成重複換算：PM 2026-07-24 明確「特殊公式是非匯率算法、僅是特殊公式」，
// 其常數（如 *1.03）屬單純加成係數、不含匯率換算，故與單次 ÷匯率 不衝突、無重複扣匯率問題。
// (c) 單品加價 itemMarkup、貼標費用 labelingFee 本就以目標幣別儲存，換算已於前面完成，直接相加即可。
// - D4 進位時機：只在最後換算結果進位一次。
// isManualPrice（手動單價）維持最優先、不受本次調整影響。

// 需求 R126：Section 3「全單加成 (%)」輸入框允許的字面值。
// ‧ 不含負號 ← PM 2026-08-21 拍板 Q3「不支援負數」（同時消除舊版「輸入得進 -1、但
// calcUnitPrice 的 if (globalMarkupPct > 0) 算不到」的落差）。
// ‧ `\d*\.?\d{0,3}` 允許中間態「1.」「0.」「.」，最多小數點後 3 位（Q1 PM 未填，
// 暫用建議值＝與 需求 R84 單品加價一致）。要調整位數上限只需改這裡與 QuoteEditPage.js 同名常數。
const GLOBAL_MARKUP_INPUT_RE = /^\d*\.?\d{0,3}$/;

const QuoteCreatePage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState('');

  // Section 1
  const [quoteNumber, setQuoteNumber] = useState('');
  const [quoteDate, setQuoteDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [expiryDate, setExpiryDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [companyHeader, setCompanyHeader] = useState('皇聚');
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companyModalShow, setCompanyModalShow] = useState(false);
  const [companyModalEdit, setCompanyModalEdit] = useState(null);
  const [customerInput, setCustomerInput] = useState('');
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [currency, setCurrency] = useState('TWD');
  const [exchangeRates, setExchangeRates] = useState({});
  // 需求 R97：單筆報價單的自訂匯率。
  // customRate === null → 跟隨全域匯率（現行行為）；非 null → 此單鎖定這個數值。
  // rateUnlocked 為「 解鎖中（數值變輸入框）」，rateInput 是輸入過程的原始字串 buffer
  // （沿用本 repo 貼標費／加成欄的做法，避免使用者打不出小數點）。
  const [customRate, setCustomRate] = useState(null);
  const [rateUnlocked, setRateUnlocked] = useState(false);
  const [rateInput, setRateInput] = useState('');
  const [specialFormula, setSpecialFormula] = useState('');
  const customerDebounceRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Section 2
  const [productKeyword, setProductKeyword] = useState('');
  // 需求 R85（PM 2026-07-24 留言「A」）：Section 2「產品選取」整段可收合，
  // 預設收合（AC7=A），使用者需要挑選商品時再展開。切換僅隱藏 .qc-section-body（display:none），
  // 元件不卸載，故搜尋條件 / 搜尋結果 / 已勾選商品 / 載入更多分頁狀態於收合再展開後完整保留。
  const [productPickerCollapsed, setProductPickerCollapsed] = useState(true);
  // 需求 R106 Q2=A（PM 2026-08-04「四頁統一改多選」）：系列／類別由單選字串改為
  // 多選陣列，與產品標籤一致；三組之間的關係由後端改為「任一符合」（Q1=A）。
  const [productSeries, setProductSeries] = useState([]);
  const [productCategory, setProductCategory] = useState([]);
  const [productTags, setProductTags] = useState([]);
  // 需求 R59 D5(A)：移除篩選面板 vegetarianType，不再讀取選項清單與相關 state / ref。
  const [seriesOptions, setSeriesOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [tagsDropdownOpen, setTagsDropdownOpen] = useState(false);
  const tagsDropdownRef = useRef(null);
  // 需求 R106 R6：系列／類別多選下拉各自的開／關 state + click-outside 用的 ref。
  // 呈現改用共用的 .multi-select-*（styles/multiSelectDropdown.css，四頁同一份來源），
  // 與型錄管理／產品管理／編輯頁對齊；R3 時原本沿用「標籤」用的 .tag-dropdown 已淘汰。
  const [seriesDropdownOpen, setSeriesDropdownOpen] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const seriesDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState(new Set());
  const [searchLoading, setSearchLoading] = useState(false);
  // 需求 R73（PM 2026-07-20 留言「方案Ｂ」）：載入更多分頁。
  // 後端 getAll 已支援 page/limit 並回傳 total；前端逐頁 append 累加，跨頁不清空既有結果，
  // 已勾選的 selectedProductIds（Set of _id）不受影響、跨頁選取狀態保留。
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchPage, setSearchPage] = useState(1);
  const [searchLoadingMore, setSearchLoadingMore] = useState(false);
  // 記錄「上次實際送出搜尋」的篩選字串（不含 page/limit），供「載入更多」沿用同一組條件，
  // 避免使用者改了篩選欄位但尚未按「搜尋」時，載入更多帶到不一致的條件。
  const lastSearchParamsRef = useRef('');
  // 需求 R123（PM 2026-08-19 拍板 Q1=B 空白 / Q2=B 只填一格也可搜尋 / Q3=B 清除同清空
  // / Q4=A 沿用「即時 state + 按鈕觸發」）：Section 2 第二列新增「建立時間區間」欄位。
  // 預設空白（Q1=B）→ 兩個 state 初值皆為空字串；驗證與送出以 validateProductDateRange 為單一來源
  // （見 utils/dateRange.js），與 handleProductSearch 一致（Q4=A 只在按下「搜尋」時做跨度驗證）。
  const [productCreatedFrom, setProductCreatedFrom] = useState('');
  const [productCreatedTo, setProductCreatedTo] = useState('');
  const [productDateError, setProductDateError] = useState('');

  // Section 3
  const [items, setItems] = useState([]);
  const [globalMarkup, setGlobalMarkup] = useState(0);
  // 需求 R126：全單加成輸入框的「原始字串 buffer」。null＝沒在編輯（畫面直接顯示數字 state），
  // 非 null＝使用者正在輸入，畫面必須顯示這個字面值，才能保留「1.」「0.」這類小數中間態。
  // （沿用 需求 R84 單品加價的既有範式，見本檔 itemMarkup 輸入框 _editingItemMarkup。）
  const [editingGlobalMarkup, setEditingGlobalMarkup] = useState(null);
  const [labelingFees, setLabelingFees] = useState({});
  const [infoPanel, setInfoPanel] = useState(null); // index of open product info panel

  // Section 4
  const [transactionNotes, setTransactionNotes] = useState('');
  // 需求 R143（PM 2026-08-29 Q1=A 非必填）：與交易條件並列的第二欄「備註」。
  // 對應 Quote schema 的 internalNotes（刻意不叫 notes，品項子文件已佔用該名稱）。
  const [internalNotes, setInternalNotes] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [salesUsers, setSalesUsers] = useState([]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  // 需求 R119（Q1=B）：本頁不再有列印入口，showExportMenu / exportRef 已零引用，一併移除。
  const [toasts, setToasts] = useState([]);
  const [editingField, setEditingField] = useState(null); // { idx, field }
  const imageInputRef = useRef(null);
  const imageUploadCtxRef = useRef(null); // { productId } target for upload

  const { can } = usePermission(user);
  const canInlineEdit = can('quote.create') || can('quote.edit');

  const pushToast = useCallback((type, message) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  }, []);

  // Auth
  useEffect(() => {
    const t = localStorage.getItem('token');
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    if (!t || !u._id) { navigate('/login'); return; }
    setToken(t);
    setUser(u);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const fetchCompanies = useCallback(() => {
    if (!token) return;
    fetch(`${API}/companies`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (Array.isArray(d)) setCompanyOptions(d); })
      .catch(() => {});
  }, [token]);

  // Fetch initial data
  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };

    // Next quote number
    fetch(`${API}/quotes/next-number`, { headers })
      .then(r => r.json())
      .then(d => setQuoteNumber(d.quoteNumber || ''))
      .catch(() => {});

    // Exchange rates
    fetch(`${API}/exchange-rates`, { headers })
      .then(r => r.json())
      .then(d => {
        const map = {};
        if (Array.isArray(d)) {
          d.forEach(r => { map[r.key] = r.rate; });
        } else if (d && typeof d === 'object') {
          Object.assign(map, d);
        }
        setExchangeRates(map);
      })
      .catch(() => {});

    // Companies
    fetchCompanies();

    // Product options
    fetch(`${API}/products/options?type=series`, { headers })
      .then(r => r.json())
      .then(d => setSeriesOptions(Array.isArray(d) ? d.map(o => o.value) : []))
      .catch(() => {});
    fetch(`${API}/products/options?type=category`, { headers })
      .then(r => r.json())
      .then(d => setCategoryOptions(Array.isArray(d) ? d.map(o => o.value) : []))
      .catch(() => {});
    fetch(`${API}/products/options?type=tag`, { headers })
      .then(r => r.json())
      .then(d => setTagOptions(Array.isArray(d) ? d : []))
      .catch(() => {});
    // 需求 R59：不再抓取 vegetarianType 選項；filter panel 與品項備註都改純文字。

    // Labeling fees
    fetch(`${API}/labeling-fees`, { headers })
      .then(r => r.json())
      .then(d => {
        const map = {};
        if (Array.isArray(d)) {
          d.forEach(f => { map[f.key] = f.fee; });
        } else if (d && typeof d === 'object') {
          Object.assign(map, d);
        }
        setLabelingFees(map);
      })
      .catch(() => {});

    // Sales users
    fetch(`${API}/users`, { headers })
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : (d.users || []);
        setSalesUsers(list.filter(u => u.role === 'sales' || u.role === 'manager'));
      })
      .catch(() => {});

    // Initial product search（載入第一頁；後端回傳 total 供「載入更多」判斷）
    setSearchLoading(true);
    lastSearchParamsRef.current = '';
    fetch(`${API}/products?limit=${PRODUCT_PAGE_SIZE}&page=1`, { headers })
      .then(r => r.json())
      .then(d => {
        setSearchResults(Array.isArray(d.products) ? d.products : []);
        setSearchTotal(typeof d.total === 'number' ? d.total : (Array.isArray(d.products) ? d.products.length : 0));
        setSearchPage(1);
      })
      .catch(() => {})
      .finally(() => setSearchLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Customer search debounce
  const searchCustomers = useCallback((kw) => {
    if (!kw.trim() || !token) { setCustomerSuggestions([]); setShowSuggestions(false); return; }
    fetch(`${API}/customers?keyword=${encodeURIComponent(kw)}&limit=10`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => {
        const list = d.customers || d || [];
        setCustomerSuggestions(Array.isArray(list) ? list : []);
        setShowSuggestions(true);
      })
      .catch(() => {});
  }, [token]);

  const handleCustomerInputChange = (e) => {
    const val = e.target.value;
    setCustomerInput(val);
    setSelectedCustomer(null);
    if (customerDebounceRef.current) clearTimeout(customerDebounceRef.current);
    customerDebounceRef.current = setTimeout(() => searchCustomers(val), 300);
  };

  const handleSelectCustomer = (cust) => {
    setSelectedCustomer(cust);
    setCustomerInput(cust.name);
    setShowSuggestions(false);
    setCustomerSuggestions([]);
  };

  // 需求 R35：客戶變更時，同步刷新各列 defaultPriceTier 作為新的比較基準。
  // Why: 既有列若不更新 defaultPriceTier，換客戶後紅點會以「舊客戶的預設階」為基準，
  // 導致判斷失準（明明等於新客戶預設卻被標為已修改，或反之）。
  // 需求 R103：基準改為逐列計算（同一張單內不同成本來源的列，預設階數可以不同）。
  // 需求 R103（PM 2026-08-03 Q8=是 / Q9）：改用 applyDefaultTier —— 非手動列的階數跟著
  // 新分級走並重算單價；手動列（Q8=是）保住使用者選的階數，只換比較基準。過去只刷基準、
  // 不動 priceTier，換完客戶會留下階數還停在舊分級、單價也跟著舊階數，卻因 Q9 把紅點綁
  // 在手動旗標而不再警示的靜默錯價。
  // 不進場條件：沒有分級時不動。使用者在客戶欄輸入時 selectedCustomer 會被清成 null
  // （handleCustomerInputChange），若照 Q2=A 的 1 階跑，打字打到一半整單非手動列就會
  // 掉到 1 階並跟著降價；維持原值、基準也不刷，等真的選到客戶再一次算對。
  useEffect(() => {
    if (!selectedCustomer?.level) return;
    setItems(prev => {
      if (!prev.length) return prev;
      let changed = false;
      const next = prev.map(it => {
        const applied = applyDefaultTier(it, selectedCustomer?.level);
        if (applied.defaultPriceTier === it.defaultPriceTier
          && applied.priceTier === it.priceTier
          && applied.isManualPriceTier === !!it.isManualPriceTier) return it;
        changed = true;
        if (applied.priceTier === it.priceTier || applied.isManualPrice) return applied;
        const rate = getCurrentRate();
        return { ...applied, unitPrice: calcUnitPrice(applied, rate, specialFormula, currency, globalMarkup) };
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer?.level]);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Get current exchange rate for selected currency
  // 需求 R97：自訂匯率優先於全域匯率。calcUnitPrice 的算式完全不動，只是代入的 rate 換人（AC4）。
  const getCurrentRate = useCallback(() => {
    if (currency === 'TWD') return 1;
    if (customRate != null) return customRate;
    return getGlobalRate(currency, exchangeRates);
  }, [currency, customRate, exchangeRates]);

  // Get labeling fee for mode and currency
  const getLabelingFee = useCallback((mode) => {
    if (mode === 'none') return 0;
    if (mode === 'auto') {
      if (currency === 'TWD') return labelingFees['label_twd'] || 0;
      if (currency === 'HKD') return labelingFees['label_hkd'] || 0;
      if (currency === 'USD') return labelingFees['label_usd'] || 0;
      if (currency === 'EUR') return labelingFees['label_eur'] || 0;
    }
    return 0;
  }, [currency, labelingFees]);

  // Recalculate a single item's unitPrice
  const recalcItem = useCallback((item) => {
    if (item.isManualPrice) return item;
    const rate = getCurrentRate();
    const labelFee = item.labelingMode === 'auto'
      ? getLabelingFee('auto')
      : (item.labelingMode === 'manual' ? (item.labelingFee || 0) : 0);
    const updatedItem = { ...item, labelingFee: labelFee };
    const newPrice = calcUnitPrice(updatedItem, rate, specialFormula, currency, globalMarkup);
    return { ...updatedItem, unitPrice: newPrice };
  }, [getCurrentRate, getLabelingFee, specialFormula, currency, globalMarkup]);

  // Recalculate all items when formula/currency/rates/markup change
  // 需求 R97：customRate 一併列入依賴 → 上鎖套用自訂匯率後所有明細立即以新匯率重跑
  // calcUnitPrice（AC2）；recalcItem 開頭的 isManualPrice 早退保證手動單價列不被覆寫（AC3）。
  useEffect(() => {
    if (items.length === 0) return;
    setItems(prev => prev.map(item => recalcItem(item)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, specialFormula, exchangeRates, customRate, labelingFees, globalMarkup]);

  // 需求 R97：/ 切換。解鎖 → 帶入目前匯率成為可輸入欄位；再點一次 → 驗證後上鎖並生效。
  // Q1=B：不做角色判斷，凡是進得到本頁（可建立報價單）的人都能解鎖。
  const toggleRateLock = () => {
    if (rateUnlocked) {
      const result = validateCustomRate(rateInput, currency, exchangeRates);
      if (!result.ok) { alert(result.message); return; } // 驗證未過維持解鎖，不套用（AC12）
      // 需求 R97 R2：若最終數值與該幣別的全域匯率完全相同（例如解鎖後未動就上鎖，或輸入
      // 與目前全域相同的值），視為「不自訂」→ 走 restoreGlobalRate 清狀態。否則會出現
      // isCustomExchangeRate=true 但值與全域相同的幽靈存檔，日後全域變動時反被凍住此舊值，
      // 且畫面會掛出無意義的紅色驚嘆號（Q3 語意上「!」是與全域不同的提示）。
      if (result.value === getGlobalRate(currency, exchangeRates)) {
        restoreGlobalRate();
        return;
      }
      setCustomRate(result.value);
      setRateUnlocked(false);
      return;
    }
    setRateInput(String(getCurrentRate()));
    setRateUnlocked(true);
  };

  // 需求 R97（Q3）：紅色驚嘆號比照單價那顆，點擊即還原成全域匯率。
  const restoreGlobalRate = () => {
    setCustomRate(null);
    setRateUnlocked(false);
    setRateInput('');
  };

  // 需求 R97（Q2=A）：切換報價幣別時清除自訂匯率並回到上鎖，改用新幣別的全域匯率。
  const handleCurrencyChange = (nextCurrency) => {
    setCurrency(nextCurrency);
    restoreGlobalRate();
  };

  // 依目前篩選欄位組出「不含 page/limit」的條件字串（載入更多沿用同一組條件）
  //
  // 需求 R131（PM 2026-08-21 拍板）：
  // 「報價單新增／編輯的『新增產品』部分，篩選欄位皆用 OR。」
  // Q4=A：「篩選欄位皆用 OR」**包含**建立時間區間（需求 R123 新加的兩格日期）——只填日期即
  // 列出區間內全部產品，且與關鍵字／系列／類別／標籤互為 OR。
  // Q5  ：五個條件都沒填就按搜尋＝不加任何條件、列出全部（未下架）產品 → 維持現況。
  // 本頁與編輯頁必須送出**完全相同**的 orFields（AC10：同條件同資料，兩頁筆數必須一致）。
  const buildProductFilterParams = () => {
    const params = new URLSearchParams();
    // 需求 R131：系列／類別／標籤／關鍵字／建立時間區間 五項併成同一包 OR（任一符合即列出）。
    // 下架排除（後端預設 productStatus $ne delisted）不在此清單內，永遠是頂層 AND（AC12）。
    params.set('orFields', 'series,category,tags,keyword,created');
    if (productKeyword) params.set('keyword', productKeyword);
    // 需求 R106：三組皆多選，逗號串送出；後端 buildProductListFilter 以 $in 精確比對（同欄任一符合）。
    if (productSeries.length > 0) params.set('series', productSeries.join(','));
    if (productCategory.length > 0) params.set('category', productCategory.join(','));
    if (productTags.length > 0) params.set('tags', productTags.join(','));
    // 需求 R123 Q2=B：只填一格也可搜尋（另一格為空即不送），後端 buildProductListFilter 已支援
    // createdFrom/createdTo 分別為 $gte / $lte（含 T23:59:59.999Z 上界）。
    // 需求 R131 Q4=A：本頁的這段區間會被併進上面那包 OR（其他頁仍是頂層 AND）。
    if (productCreatedFrom) params.set('createdFrom', productCreatedFrom);
    if (productCreatedTo) params.set('createdTo', productCreatedTo);
    return params;
  };

  // Product search（第一頁；重置分頁狀態）
  const handleProductSearch = async () => {
    if (!token) return;
    // 需求 R123 Q4=A：維持「即時 state + 按鈕觸發」；跨度驗證只在按下「搜尋」當下做，
    // 驗證失敗 setDateError 並直接 return，不打 API。
    const err = validateProductDateRange(productCreatedFrom, productCreatedTo);
    if (err) {
      setProductDateError(err);
      return;
    }
    setProductDateError('');
    setSearchLoading(true);
    const filter = buildProductFilterParams();
    lastSearchParamsRef.current = filter.toString();
    const params = new URLSearchParams(filter);
    params.set('limit', PRODUCT_PAGE_SIZE);
    params.set('page', 1);

    try {
      const res = await fetch(`${API}/products?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setSearchResults(data.products || []);
      setSearchTotal(typeof data.total === 'number' ? data.total : (data.products?.length || 0));
      setSearchPage(1);
    } catch {
      setSearchResults([]);
      setSearchTotal(0);
      setSearchPage(1);
    } finally {
      setSearchLoading(false);
    }
  };

  // 需求 R73（方案Ｂ 載入更多）：抓下一頁並 append，跨頁去重、保留既有勾選
  const handleLoadMoreProducts = async () => {
    if (!token || searchLoadingMore) return;
    const nextPage = searchPage + 1;
    setSearchLoadingMore(true);
    const params = new URLSearchParams(lastSearchParamsRef.current || '');
    params.set('limit', PRODUCT_PAGE_SIZE);
    params.set('page', nextPage);

    try {
      const res = await fetch(`${API}/products?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const incoming = Array.isArray(data.products) ? data.products : [];
      setSearchResults(prev => {
        const seen = new Set(prev.map(p => p._id));
        return [...prev, ...incoming.filter(p => !seen.has(p._id))];
      });
      if (typeof data.total === 'number') setSearchTotal(data.total);
      setSearchPage(nextPage);
    } catch {
      // 載入失敗維持現況，不清空已載入結果
    } finally {
      setSearchLoadingMore(false);
    }
  };

  const handleClearProductSearch = () => {
    setProductKeyword('');
    setProductSeries([]);
    setProductCategory([]);
    setProductTags([]);
    // 需求 R123 Q3=B：清除同清空（Q1=B 預設也是空，所以「清空」＝回到預設）；同步清錯誤訊息。
    setProductCreatedFrom('');
    setProductCreatedTo('');
    setProductDateError('');
    setSearchResults([]);
    setSelectedProductIds(new Set());
    setSearchTotal(0);
    setSearchPage(1);
    lastSearchParamsRef.current = '';
  };

  const toggleProductTag = (value) => {
    setProductTags((prev) => prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value]);
  };

  // 需求 R106 Q2=A：系列／類別多選切換（勾了再點＝取消），語意與 toggleProductTag 相同。
  const toggleProductSeries = (value) => {
    setProductSeries((prev) => prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]);
  };
  const toggleProductCategory = (value) => {
    setProductCategory((prev) => prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value]);
  };

  const toggleProductSelect = (id) => {
    setSelectedProductIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // 需求 R73 AC5（比照 需求 R62 全選 bug 教訓）：表頭全選 = 「全選/取消 目前已載入的所有列」，
  // 用 every-id 成員判斷而非 size 相等，且只增減已載入列的 id，不動其他狀態，避免跨頁誤清空。
  const isAllLoadedSelected = searchResults.length > 0 && searchResults.every(p => selectedProductIds.has(p._id));

  const toggleSelectAll = () => {
    setSelectedProductIds(prev => {
      const next = new Set(prev);
      if (isAllLoadedSelected) {
        searchResults.forEach(p => next.delete(p._id));
      } else {
        searchResults.forEach(p => next.add(p._id));
      }
      return next;
    });
  };

  // 需求 R136（PM 2026-08-24 拍板）：「已在報價清單中」的產品 id 集合。
  // Q5＝在搜尋結果列事前標示「此品項已在報價清單中」（PM 逐字：「版面更乾淨，提醒更直覺」），
  // → 不另外跳 toast／alert（Q2 時機／Q3 形式／Q4 多筆文案依 PM 指示不需回答）。
  // 本集合只供標示用；Q1=B 之後不得再拿來過濾要加入的品項（見 handleAddProducts）。
  // 隨 items 即時更新：加入後畫面重繪，該列的標示立刻出現。
  const quotedProductIds = buildQuotedProductIdSet(items);

  // Add selected products to items
  const handleAddProducts = () => {
    const toAdd = searchResults.filter(p => selectedProductIds.has(p._id));

    // 需求 R136（PM 2026-08-24 拍板 Q1=B 逐字：
    // 「照樣加入，允許同一產品在清單出現兩列，只是提醒。」）
    // 原本這裡有一行 `.filter(p => !existingIds.has(p._id))` 把重複品項**靜默丟棄**——
    // 使用者按下按鈕後只看到勾選被清空、清單筆數沒變，完全無從得知原因。
    // 依 Q1=B 移除該過濾：重複品項照樣加入成第二列，提醒改在 Section 2 搜尋結果列事前呈現
    // （quotedProductIds + DUPLICATE_ITEM_HINT），完全不擋、不中斷整批操作。
    // 日後若有人「順手」把去重加回來，等於推翻 PM 的 Q1=B，須先重新拍板。
    const newItems = toAdd
      .map(p => {
        // 需求 R74：加入報價單時即把 legacy 'distributor' 正規化為 'distributor_0'，
        // 使 item.costSource 恆為帶索引格式 → 抽屜成本來源 radio 能正確高亮到「經銷1」、
        // defaultCostSource 比較基準一致（否則舊值 'distributor' 對不上任何 radio）。
        const costSource = normalizeCostSource(p.costSource || 'factory');
        // 需求 R103（AC5）：預設階數逐列判定，同一張單內工廠列與經銷列可有不同階數。
        const defaultTier = getDefaultPriceTier(selectedCustomer?.level, costSource);
        const baseCost = getBaseCostFromSource(p, costSource);
        const labelingMode = p.canLabel ? 'auto' : 'none';
        const labelFee = labelingMode === 'auto' ? getLabelingFee('auto') : 0;

        const draft = {
          product: p._id,
          productCode: p.productCode || '',
          barcode: p.barcode || '',
          productName: p.productName || '',
          englishName: p.englishName || '',
          productImage: p.productImage || '',
          salesNotes: p.salesNotes || '',
          spec: p.spec || '',
          // 需求 R121 R2：保存期限跟著品項快照一起存，匯出才不必依賴 GET /quotes/:id 的 populate。
          shelfLife: p.shelfLife || '',
          // 需求 R145（PM 2026-08-29 Q6=A）：系列存成「加入當下」的快照，之後產品改系列
          // 不影響已建立的報價單。冷凍系列（冷凍品項／冷凍散裝原料）的四階材積係數為 23。
          series: p.series || '',
          pcsPerBox: p.pcsPerBox || null,
          volumeCuft: p.volumeCuft || null,
          volumeMethod: p.volumeMethod || 'auto',
          priceTier: defaultTier,
          // 需求 R35（PM 2026-05-22 留言）：
          // 「階數的改變一樣會改變單價，因此驚嘆號的觸發與設定在單價旁的驚嘆號相同即可」
          // Why: 階數變動會經由 calcUnitPrice 影響單價，PM 指示不另設階數旁的紅點，
          // 直接讓既有「單價旁」的 price-modified-dot 也將「階數 ≠ 預設」納入觸發條件。
          // How to apply: 加入產品時記錄當下預設階為比較基準；改回該值警示自動消失。
          defaultPriceTier: defaultTier,
          // 需求 R103（Q8=A）：新加入的品項預設非手動；使用者在 updateItem 手動改階數才置 true。
          isManualPriceTier: false,
          costSource,
          defaultCostSource: costSource,
          baseCost,
          // 需求 R170（PM 2026-09-15 Q1=A／Q3=A）：MOQ 跟著上面同一個 costSource 走
          // （產品主檔勾的「使用此成本報價」），與 baseCost 同源；原本寫死 `p.factoryCost?.moq`
          // → 勾經銷的產品成本是經銷的、MOQ 卻是工廠的。該來源沒填 MOQ 就空字串（Q3=A），
          // 不回退工廠（與 需求 R104 Q2=A 切換來源的清空行為一致）。
          moq: getCostEntryFromSource(p, costSource)?.moq || '',
          itemMarkup: 0,
          labelingMode,
          labelingFee: labelFee,
          unitPrice: null,
          isManualPrice: false,
          isCostSourceChanged: false,
          customCostEnabled: false,
          customCostValue: 0,
          // 需求 R149：新加入的品項兩種自訂成本都預設關閉。
          customBoxCostEnabled: false,
          customBoxCostValue: 0,
          quantity: 0,
          vegetarianNote: p.vegetarianType || '',
          notes: '',
          _productData: p, // keep full product for cost source switching
          _origProductImage: p.productImage || '',
          _origEnglishName: p.englishName || '',
          // 需求 R118（Q8=A「清掉」）：salesNotes 不再回寫，比對用的 _origSalesNotes 快照一併移除。
          // 需求 R34（PM 2026-05-22 留言）：「此葷素備註，經修改儲存後，回寫 product」
          // 在建立報價單儲存時，若 vegetarianNote 已被使用者修改（與本欄初始值不同），需回寫 Product.vegetarianType。
          _origVegetarianType: p.vegetarianType || '',
        };

        const rate = getCurrentRate();
        draft.unitPrice = calcUnitPrice(draft, rate, specialFormula, currency, globalMarkup);
        return draft;
      });

    // 需求 R94（PM 2026-07-31 拍板 Q2/Q3）：
    // Q3「重排時機：在『按下添加到報價單』當下重排一次」
    // Q2「未來沒有手動排序問題」→ 重排不會蓋掉使用者的手動順序
    // 原本是 [...prev, ...newItems] 純接在尾端，多批次追加後全域順序＝批次區塊串接；
    // 改為把「既有品項 + 本批新品項」合併後一併依統一基準排序（見 utils/productSort.js）。
    // 只在此處排一次，不在 render 時強制排序，故使用者中途改欄位不會讓列順序跳動。
    //
    // 需求 R114（報價單品項手動排序）評估結論＝不執行，故本行維持原樣：
    // 該票要讓使用者手動調整品項順序，會與此處「合併後整份重排」直接衝突
    // （手動排好之後只要再按一次「添加到報價單」，手動順序就會被系統規則洗掉）。
    // PM 2026-08-18 留言逐字：「理解，這部分排序的確會與原本的設定衝突，
    // 本案不執行，人工丟到已取消」→ 不加上移／下移按鈕、不加拖曳、不加「已手動排序」旗標，
    // 需求 R94 Q2「未來沒有手動排序問題」的前提續成立。日後要做須由 PM 另開新票拍板。
    setItems(prev => sortQuoteItems([...prev, ...newItems]));
    // 重排會改變品項索引，infoPanel 存的是「第幾列」→ 一併關閉，避免抽屜指到別的產品。
    setInfoPanel(null);
    setSelectedProductIds(new Set());
  };

  // Update item field
  const updateItem = (index, field, value) => {
    setItems(prev => {
      const next = [...prev];
      const item = { ...next[index], [field]: value };

      // 需求 R103（Q8=是）：使用者手動改階數 → 記旗標，下次開編輯頁時 Q3=B 不覆寫這列。
      // 抽屜切成本來源不走本函式（走 handleCostSourceChange），該處自己覆寫階數與旗標
      // （需求 R104 Q1=A）；本函式只處理使用者在階數欄「重新選擇」的情形＝PM 說的驚嘆號來源。
      // Q9（PM：「紅色驚嘆號是指又手動調整（非預設）就會出現」）：applyDefaultTier 內部
      // 維護不變式，選回當下規則預設值時旗標歸零、紅點跟著熄。
      const patched = field === 'priceTier'
        ? applyDefaultTier(item, selectedCustomer?.level, value)
        : item;

      // Recalculate if relevant field changed
      if (['priceTier', 'itemMarkup', 'labelingMode', 'labelingFee'].includes(field) && !patched.isManualPrice) {
        const labelFee = patched.labelingMode === 'auto'
          ? getLabelingFee('auto')
          : (patched.labelingMode === 'manual' ? (patched.labelingFee || 0) : 0);
        const itemWithFee = { ...patched, labelingFee: labelFee };
        const rate = getCurrentRate();
        itemWithFee.unitPrice = calcUnitPrice(itemWithFee, rate, specialFormula, currency, globalMarkup);
        next[index] = itemWithFee;
      } else {
        next[index] = patched;
      }
      return next;
    });
  };

  // Manual price edit
  const handleManualPrice = (index, value) => {
    setItems(prev => {
      const next = [...prev];
      next[index] = { ...next[index], unitPrice: value === '' ? null : parseFloat(value) || 0, isManualPrice: true };
      return next;
    });
  };

  // Restore auto price
  const handleRestoreAutoPrice = (index) => {
    setItems(prev => {
      const next = [...prev];
      const item = { ...next[index], isManualPrice: false };
      const rate = getCurrentRate();
      item.unitPrice = calcUnitPrice(item, rate, specialFormula, currency, globalMarkup);
      next[index] = item;
      return next;
    });
  };

  // Remove item
  const removeItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  // Sync inline-edited product field across all items sharing the same product id
  const syncProductFieldAcrossItems = useCallback((productId, field, value) => {
    if (!productId) return;
    setItems(prev => prev.map(it => {
      const itProductId = typeof it.product === 'object' ? it.product?._id : it.product;
      if (itProductId === productId) {
        return { ...it, [field]: value };
      }
      return it;
    }));
  }, []);

  // Trigger image upload for a specific item
  const triggerImageUpload = (idx) => {
    if (!canInlineEdit) return;
    const item = items[idx];
    const productId = typeof item.product === 'object' ? item.product?._id : item.product;
    if (!productId) {
      pushToast('error', '無法更新圖片：此品項缺少對應產品');
      return;
    }
    imageUploadCtxRef.current = { productId };
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
      imageInputRef.current.click();
    }
  };

  const handleImageFileChange = async (e) => {
    const file = e.target.files?.[0];
    const ctx = imageUploadCtxRef.current;
    if (!file || !ctx?.productId || !token) return;
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await fetch(`${API}/products/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || '圖片上傳失敗');
      }
      const { url } = await res.json();
      syncProductFieldAcrossItems(ctx.productId, 'productImage', url || '');
      pushToast('success', '圖片已更新（儲存報價單時將同步寫回產品）');
    } catch (err) {
      pushToast('error', err.message || '圖片上傳失敗');
    } finally {
      imageUploadCtxRef.current = null;
    }
  };

  const handleCostSourceChange = (index, newSource) => {
    setItems(prev => {
      const next = [...prev];
      const item = { ...next[index] };
      const product = item._productData;
      const defaultSource = item.defaultCostSource ?? (product?.costSource || 'factory');
      item.costSource = newSource;
      // 需求 R32：切換成本來源時，customCost 狀態保留，但需重新以 effectiveBaseCost 計算 baseCost。
      item.baseCost = getEffectiveBaseCost(item);
      item.isCostSourceChanged = newSource !== defaultSource;
      // 需求 R104 Q1=A（PM 2026-08-04：「一律覆寫成新來源的預設階。但一旦『重新選擇』意指
      // 非預設，則啟動驚嘆號」）——本票明文推翻 需求 R103 Q1=B「切成本來源時階數不連動」。
      // ‧ 必須在 item.costSource = newSource 之後才呼叫：applyDefaultTier 讀的是 item 上的
      // costSource；順序顛倒會拿舊來源算階。
      // ‧ 第四參 forceDefault=true → 連手動列（需求 R103 Q8=A 保住的那種）也覆寫成新來源
      // 預設階、isManualPriceTier 歸零 → 紅點的「價格階已調整為非預設」這一行不會亮
      // （「成本來源已變更」那一行仍可能亮，兩者是不同判斷）。使用者若在覆寫後再去階數欄
      // 手動挑非預設值，旗標與紅點才重新成立，正是 PM 補述的「重新選擇意指非預設」。
      // ‧ Q4 保護：拿不到客戶分級（未綁客戶／使用者正在客戶欄打字）時整段不進場——
      // Q2=A 的預設是 1 階，硬套會把既有單的階數無聲打成 1 階並降價；此時維持原階數與
      // 原基準不動（＝現行保護）。已審核唯讀單走不到本函式（抽屜的 radio disabled）。
      if (selectedCustomer?.level) {
        const tiered = applyDefaultTier(item, selectedCustomer.level, undefined, true);
        item.priceTier = tiered.priceTier;
        item.defaultPriceTier = tiered.defaultPriceTier;
        item.isManualPriceTier = tiered.isManualPriceTier;
      }
      // Update MOQ from new cost entry
      // 需求 R104 Q2=A（PM：「切到該來源沒有填 MOQ 的成本來源時，MOQ 清空」）：
      // 原本寫 `if (newEntry?.moq)` ＝新來源沒 MOQ 就沿用舊來源的值，畫面會出現
      // 「工廠的 MOQ 掛在經銷來源上」的錯配。改成一律覆寫，缺值落回空字串（schema 為 String）。
      // 需求 R170（AC8）：取 entry 改走 quoteUnitPrice.js getCostEntryFromSource，與 handleAddProducts 同源。
      const newEntry = getCostEntryFromSource(product, newSource);
      item.moq = newEntry?.moq || '';
      const rate = getCurrentRate();
      if (!item.isManualPrice) {
        item.unitPrice = calcUnitPrice(item, rate, specialFormula, currency, globalMarkup);
      }
      next[index] = item;
      return next;
    });
  };

  // 需求 R32：自定成本開關／數值變動共用更新邏輯。
  const updateCustomCost = (index, patch) => {
    setItems(prev => {
      const next = [...prev];
      const item = { ...next[index], ...patch };
      item.baseCost = getEffectiveBaseCost(item);
      if (!item.isManualPrice) {
        const rate = getCurrentRate();
        item.unitPrice = calcUnitPrice(item, rate, specialFormula, currency, globalMarkup);
      }
      next[index] = item;
      return next;
    });
  };

  // 需求 R149 Q1=A（互斥）：勾其中一個自訂成本，另一個自動取消，同時只有一個生效。
  // 互斥寫在 handler（而不是只靠 UI 的 checked）是刻意的：getEffectiveBaseCost 的
  // 防禦性順序雖然保證不會算出兩種結果，但畫面上同時亮兩個勾會讓使用者誤以為兩者相加。
  const handleCustomCostToggle = (index, enabled) => {
    updateCustomCost(index, enabled
      ? { customCostEnabled: true, customBoxCostEnabled: false }
      : { customCostEnabled: false });
  };

  const handleCustomCostChange = (index, rawValue) => {
    if (rawValue === '' || rawValue == null) {
      updateCustomCost(index, { customCostValue: 0, _editingCustomCost: '' });
      return;
    }
    const n = Number(rawValue);
    if (!Number.isFinite(n) || n < 0) return;
    updateCustomCost(index, { customCostValue: n, _editingCustomCost: rawValue });
  };

  // 需求 R149：自訂箱成本。Q4=A —— 箱入數空／≤0 的品項換算不出包成本，handler 層直接擋下，
  // 不倚賴 UI 的 disabled（比照 handleCostSourceChange 的既有寫法）。
  const handleCustomBoxCostToggle = (index, enabled) => {
    if (enabled && !canUseCustomBoxCost(items[index])) return;
    updateCustomCost(index, enabled
      ? { customBoxCostEnabled: true, customCostEnabled: false }
      : { customBoxCostEnabled: false });
  };

  const handleCustomBoxCostChange = (index, rawValue) => {
    if (rawValue === '' || rawValue == null) {
      updateCustomCost(index, { customBoxCostValue: 0, _editingCustomBoxCost: '' });
      return;
    }
    const n = Number(rawValue);
    if (!Number.isFinite(n) || n < 0) return;
    updateCustomCost(index, { customBoxCostValue: n, _editingCustomBoxCost: rawValue });
  };

  // Labeling bulk actions
  const setAllLabeling = (mode) => {
    setItems(prev => prev.map(item => {
      if (item.isManualPrice) return item;
      const labelFee = mode === 'auto' ? getLabelingFee('auto') : 0;
      const updated = { ...item, labelingMode: mode, labelingFee: labelFee };
      const rate = getCurrentRate();
      updated.unitPrice = calcUnitPrice(updated, rate, specialFormula, currency, globalMarkup);
      return updated;
    }));
  };

  // Apply global markup to all non-manual items (recalculation triggered via useEffect)
  const applyGlobalMarkup = (markupPct) => {
    setGlobalMarkup(markupPct);
  };

  // Fetch last transaction notes
  const fetchLastTransaction = async () => {
    if (!selectedCustomer?._id || !token) return;
    try {
      const res = await fetch(
        `${API}/quotes?customer=${selectedCustomer._id}&limit=1&sort=-createdAt`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      const lastQuote = (data.quotes || [])[0];
      if (lastQuote?.transactionNotes) {
        setTransactionNotes(lastQuote.transactionNotes);
      }
    } catch {}
  };

  // 需求 R119（Q1=B）：本頁已無列印入口，原「Click-outside to close export menu」監聽一併移除。

  // Click-outside to close product tag / vegetarian dropdowns
  // 需求 R106：系列／類別多選下拉一併納入同一個 click-outside 監聽。
  useEffect(() => {
    const handler = (e) => {
      if (tagsDropdownRef.current && !tagsDropdownRef.current.contains(e.target)) {
        setTagsDropdownOpen(false);
      }
      if (seriesDropdownRef.current && !seriesDropdownRef.current.contains(e.target)) {
        setSeriesDropdownOpen(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target)) {
        setCategoryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // 需求 R119（PM 2026-08-18 拍板 Q1=B）：
  // 「A ＋ 建立頁（尚未存檔）也一律不給列印」——本頁的報價單還沒存檔（等同草稿，
  // 連單號都可能還沒定案），依規則「草稿 → 不可列印報價單」，故本頁**完全沒有**列印入口。
  // 因此原本的 buildExportQuoteObj / handleExportExcel / handleExportPDF（需求 R37 起共用
  // quoteExcelExport・quotePdfExport 的那一組）以及底部「列印」按鈕、匯出下拉選單、
  // showExportMenu / exportRef 與其 click-outside 監聽，全數移除——留著就是零引用的死碼，
  // 也會讓日後有人以為這頁「只是暫時藏起來」。
  // 要列印請先存檔 → 送審（狀態轉 pending）→ 於詳情頁或編輯頁列印。
  // （對照：QuoteEditPage 的列印按鈕改為 canPrint = pending || approved；
  // QuoteDetailPage 原本就是同一條件。）

  // Save quote (returns created quote data or null on failure)
  const handleSave = async (andSubmit = false) => {
    setError('');
    if (!quoteDate) { setError('請填寫報價日期'); return null; }
    if (!expiryDate) { setError('請填寫截止日期'); return null; }
    if (expiryDate < quoteDate) { setError('截止日期不能早於報價日期'); return null; }
    if (!customerInput.trim()) { setError('請填寫客戶名稱'); return null; }
    // 需求 R143 Q3=A：欄位標籤改為「交易條件」，錯誤訊息一併對齊（原為「請填寫交易條件備註」）。
    // 必填規則本身不變（AC3）——PM 只改名稱，沒有改行為。
    // 新增的「備註」是 Q1=A 非必填，刻意不在這裡加任何檢查（AC12 同理，建立者也不驗證）。
    if (!transactionNotes.trim()) { setError('請填寫交易條件'); return null; }
    if (!contactPerson) { setError('請選擇聯絡人 (業務)'); return null; }

    setSaving(true);
    try {
      const payload = {
        quoteDate,
        expiryDate,
        companyHeader,
        customer: selectedCustomer?._id || null,
        customerName: customerInput,
        currency,
        exchangeRate: getCurrentRate(),
        // 需求 R97（AC7 / Q5=A）：自訂匯率連同旗標一起存，編輯頁才分得出「刻意鎖定的匯率」
        // 與「只是存下當時的全域匯率」。
        isCustomExchangeRate: customRate != null,
        specialFormula,
        globalMarkup: globalMarkup || 0,
        items: items.map(item => ({
          product: item.product || null,
          productCode: item.productCode,
          barcode: item.barcode || '',
          productName: item.productName,
          englishName: item.englishName,
          productImage: item.productImage || '',
          salesNotes: item.salesNotes || '',
          spec: item.spec,
          // 需求 R121 R2：保存期限存入品項快照（Quote.items.shelfLife），匯出不再只靠 populate。
          shelfLife: getItemShelfLife(item),
          // 需求 R145 Q6=A：系列快照隨單存檔（Quote.items.series），冷凍判定之後不再依賴實時產品。
          series: resolveItemSeries(item),
          pcsPerBox: item.pcsPerBox,
          volumeCuft: item.volumeCuft,
          volumeMethod: item.volumeMethod || 'auto',
          priceTier: item.priceTier,
          // 需求 R103（Q8=A）：階數是否被人手改過要跟著存，否則編輯頁重開時 Q3=B 會把它算掉。
          isManualPriceTier: !!item.isManualPriceTier,
          costSource: item.costSource,
          baseCost: item.baseCost,
          moq: item.moq,
          itemMarkup: item.itemMarkup,
          labelingMode: item.labelingMode,
          labelingFee: item.labelingFee,
          unitPrice: item.unitPrice,
          isManualPrice: item.isManualPrice,
          isCostSourceChanged: item.isCostSourceChanged,
          customCostEnabled: !!item.customCostEnabled,
          customCostValue: Number(item.customCostValue) || 0,
          // 需求 R149（AC14）：新欄位進存檔 payload。schema 已宣告，不會被 strict mode 丟棄。
          customBoxCostEnabled: !!item.customBoxCostEnabled,
          customBoxCostValue: Number(item.customBoxCostValue) || 0,
          quantity: item.quantity,
          vegetarianNote: item.vegetarianNote,
          notes: item.notes,
        })),
        transactionNotes,
        // 需求 R143（AC4）：備註隨單存檔。建立者不進 payload——後端 create 一律以
        // req.user._id 寫入 createdBy（quoteController.js），前端送什麼都不算數（AC12／AC13）。
        internalNotes,
        contactPerson: contactPerson || null,
      };

      const res = await fetch(`${API}/quotes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || '儲存失敗');
      }

      const created = await res.json();

      if (andSubmit && created._id) {
        const statusRes = await fetch(`${API}/quotes/${created._id}/status`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ status: 'pending' }),
        });
        if (!statusRes.ok) {
          const errData = await statusRes.json();
          throw new Error(errData.message || '送出審核失敗（報價單已儲存為草稿）');
        }
      }

      // After quote saved, write back changed product fields (productImage / englishName / vegetarianType)
      // 需求 R118：業務備註（salesNotes）已自回寫清單移除，故「只改業務備註」時不會發出
      // 任何 PUT /products/:id，也不會跳「已回寫 N 筆產品資料」（AC7）。
      // independently of the quote save flow — failures show toast but do not roll back.
      const wb = await writeBackProductFields();
      // Delay navigation so user can see toast for failures
      if (wb.failures > 0) {
        setTimeout(() => navigate(`/quotes/${created._id}`), 2800);
      } else {
        navigate(`/quotes/${created._id}`);
      }
      return created;
    } catch (err) {
      setError(err.message || '儲存失敗');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const writeBackProductFields = async () => {
    if (!token) return;
    // Group by product id and pick latest values; compare to original snapshot
    const byProduct = new Map();
    for (const it of items) {
      const pid = typeof it.product === 'object' ? it.product?._id : it.product;
      if (!pid) continue;
      byProduct.set(pid, it);
    }
    const writes = [];
    byProduct.forEach((it, pid) => {
      const updates = {};
      if ((it.productImage || '') !== (it._origProductImage || '')) updates.productImage = it.productImage || '';
      if ((it.englishName || '') !== (it._origEnglishName || '')) updates.englishName = it.englishName || '';
      // 需求 R118（PM 2026-08-18 拍板 Q1=B「兩頁一起停」、Q2「只停業務備註一欄」）：
      // 任務描述只寫「編輯報價單」，但 PM Q1 選 B → 建立報價單頁一起停，兩頁行為一致。
      // 業務備註（salesNotes）不再回寫 Product.salesNotes——這裡「刻意」沒有 updates.salesNotes。
      // Q3=A: 值仍存在報價單上（payload 的 items[].salesNotes，見 :1248 / :1312）。
      // 型錄管理→批量修改的回寫路徑（CatalogPage.js:85 writeBack: true）不受影響，仍照舊回寫。
      // 請勿再把 salesNotes 加回來——tests/quoteSalesNotesNoWriteBack.fixture.js 會擋。
      // 需求 R34（PM 2026-05-22 留言）：「此葷素備註，經修改儲存後，回寫 product」
      // 比對快照後若 vegetarianNote 與初始 Product.vegetarianType 不同，回寫至 Product.vegetarianType。
      if ((it.vegetarianNote || '') !== (it._origVegetarianType || '')) updates.vegetarianType = it.vegetarianNote || '';
      if (Object.keys(updates).length > 0) writes.push({ pid, updates, label: it.productCode || it.productName || pid });
    });
    if (writes.length === 0) return { okCount: 0, failures: 0 };
    const results = await Promise.allSettled(writes.map(async ({ pid, updates, label }) => {
      const res = await fetch(`${API}/products/${pid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const msg = res.status === 403
          ? `報價單已儲存但產品回寫失敗（無權限）：${label}`
          : `報價單已儲存但產品回寫失敗（${label}）：${data.message || res.status}`;
        throw new Error(msg);
      }
      return label;
    }));
    let okCount = 0;
    let failures = 0;
    results.forEach(r => {
      if (r.status === 'fulfilled') {
        okCount += 1;
      } else {
        failures += 1;
        pushToast('error', r.reason?.message || '產品回寫失敗');
      }
    });
    if (okCount > 0) pushToast('success', `已回寫 ${okCount} 筆產品資料`);
    return { okCount, failures };
  };

  const currentRate = getCurrentRate();
  // 需求 R97：全域匯率（給驚嘆號 tooltip 顯示還原後的值）與「此單是否為自訂匯率」。
  const globalRate = getGlobalRate(currency, exchangeRates);
  const isCustomRate = customRate != null;

  if (!user) return null;

  return (
    <div className="qc-page">
      <Header title="建立報價單" user={user} onLogout={handleLogout} onUserUpdate={setUser} />

      <div className="qc-container">
        {error && <div className="qc-error-banner">{error}</div>}

        {/* ========== SECTION 1: 基本資訊 ========== */}
        <section className="qc-section">
          <div className="qc-section-header">
            <div className="qc-section-header-left">
              <span className="qc-section-number">1</span>
              報價單基本資訊
            </div>
            <div className="qc-section-header-right">
              <span className="status-badge-draft">草稿</span>
            </div>
          </div>
          <div className="qc-section-body">
            {/* Row 1: 4 columns */}
            <div className="qc-form-grid qc-grid-4" style={{ marginBottom: 'var(--spacing-md)' }}>
              <div className="form-group">
                <label>報價單編號</label>
                <input className="form-control" value={quoteNumber} readOnly />
              </div>
              <div className="form-group">
                <label>報價日期 <span className="qc-required">*</span></label>
                <input
                  type="date"
                  className="form-control"
                  value={quoteDate}
                  onChange={e => setQuoteDate(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>截止日期 <span className="qc-required">*</span></label>
                <input
                  type="date"
                  className="form-control"
                  value={expiryDate}
                  min={quoteDate}
                  onChange={e => setExpiryDate(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>公司抬頭 <span className="qc-required">*</span></label>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <select
                    className="form-control"
                    value={companyHeader}
                    onChange={e => setCompanyHeader(e.target.value)}
                    style={{ flex: 1 }}
                  >
                    {companyOptions.map(c => (
                      <option key={c._id} value={c.label}>{c.label}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="company-manage-btn"
                    title="編輯公司資訊"
                    onClick={() => {
                      const co = companyOptions.find(c => c.label === companyHeader);
                      setCompanyModalEdit(co || null);
                      setCompanyModalShow(true);
                    }}
                  >&#9998;</button>
                  <button
                    type="button"
                    className="company-manage-btn"
                    title="新增公司"
                    onClick={() => {
                      setCompanyModalEdit(null);
                      setCompanyModalShow(true);
                    }}
                  >+</button>
                </div>
              </div>
            </div>
            {/* Row 2: 3 columns */}
            <div className="qc-form-grid qc-grid-3">
              <div className="form-group" ref={suggestionsRef} style={{ position: 'relative' }}>
                <label>客戶名稱 <span className="qc-required">*</span></label>
                {/*
                  需求 R113：本欄與客戶管理呼叫同一支 GET /api/customers、共用 customerController
                  的 buildCustomerFilter，keyword 一律展開成 name／customerNumber／shortName／
                  contacts.name／contacts.email 五個欄位的 $or——「可搜尋範圍」本來就已一致，
                  使用者搜不到只是因為 placeholder 沒告知可以打編號／簡稱／聯絡人／Email。
                  PM 2026-08-18 拍 Q1=A「完全比照客戶管理」、Q5「只做 D1（文案）即可結案」，
                  故此處文案與 CustomersPage.js:286 逐字相同（不另加報價單特有的「或直接填寫」語意）。
                  注意：文案雖不再提，「查無客戶時直接手填客戶名稱送出」的能力未被移除，仍照舊可用。
                */}
                <input
                  type="text"
                  className="form-control"
                  placeholder="輸入客戶名稱、編號、簡稱、聯絡人、Email..."
                  value={customerInput}
                  onChange={handleCustomerInputChange}
                  onFocus={() => customerSuggestions.length > 0 && setShowSuggestions(true)}
                  autoComplete="off"
                />
                {showSuggestions && customerSuggestions.length > 0 && (
                  <div className="qc-suggestions">
                    {customerSuggestions.map(c => (
                      <div
                        key={c._id}
                        className="qc-suggestion-item"
                        onMouseDown={() => handleSelectCustomer(c)}
                      >
                        <span className="qc-suggestion-name">{c.name}</span>
                        {c.level && <span className={`qc-level-badge qc-level-${c.level.toLowerCase()}`}>{c.level}</span>}
                        {c.country && <span className="qc-suggestion-meta">{c.country}</span>}
                      </div>
                    ))}
                  </div>
                )}
                {selectedCustomer && (
                  <div className="qc-selected-customer">
                    <span>已選: <strong>{selectedCustomer.name}</strong></span>
                    {selectedCustomer.level && (
                      <span className={`qc-level-badge qc-level-${selectedCustomer.level.toLowerCase()}`}>
                        {selectedCustomer.level}級
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>報價幣別</label>
                <select
                  className="form-control"
                  value={currency}
                  onChange={e => handleCurrencyChange(e.target.value)}
                >
                  <option value="TWD">TWD — 新台幣</option>
                  <option value="HKD">HKD — 港幣</option>
                  <option value="USD">USD — 美元</option>
                  <option value="EUR">EUR — 歐元</option>
                </select>
                {/* 需求 R97：TWD 時整區維持不顯示（AC11）。非 TWD 時  可點擊解鎖成輸入框，
                    再點一次上鎖套用；自訂生效時數值旁出現紅色驚嘆號（沿用 .price-modified-dot）。 */}
                {currency !== 'TWD' && (
                  <div className="exchange-rate-display">
                    <span className="rate-label">當前匯率:</span>
                    {rateUnlocked ? (
                      <input
                        type="text"
                        inputMode="decimal"
                        className="form-control form-control-sm rate-input"
                        value={rateInput}
                        onChange={e => { if (isRateInputAllowed(e.target.value)) setRateInput(e.target.value); }}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); toggleRateLock(); } }}
                        title={`最多 ${getRateDecimals(currency, exchangeRates)} 位小數，須大於 0`}
                        autoFocus
                      />
                    ) : (
                      <span className="rate-value">{currentRate || '(未設定)'}</span>
                    )}
                    {isCustomRate && !rateUnlocked && (
                      <span
                        className="price-modified-dot"
                        title={`此報價單使用自訂匯率（全域匯率為 ${globalRate}），點擊還原`}
                        onClick={restoreGlobalRate}
                      >!</span>
                    )}
                    <span
                      className="lock-icon rate-lock-toggle"
                      role="button"
                      title={rateUnlocked ? '點擊上鎖並套用此匯率' : '點擊解鎖以自訂此報價單的匯率'}
                      onClick={toggleRateLock}
                    >{rateUnlocked ? '🔓' : '🔒'}</span>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>特殊公式</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="例如 *1.03/3.8 （依序套用）"
                  value={specialFormula}
                  onChange={e => setSpecialFormula(e.target.value)}
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========== SECTION 2: 產品選取（需求 R85：整段可收合，預設收合）========== */}
        <section className="qc-section">
          <div
            className="qc-section-header qc-section-header--collapsible"
            role="button"
            tabIndex={0}
            aria-expanded={!productPickerCollapsed}
            onClick={() => setProductPickerCollapsed(v => !v)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setProductPickerCollapsed(v => !v);
              }
            }}
          >
            <div className="qc-section-header-left">
              <span className="qc-section-number">2</span>
              產品選取
            </div>
            <div className="qc-section-header-right">
              <span className="qc-collapse-toggle">
                <span className="qc-collapse-arrow">{productPickerCollapsed ? '▸' : '▾'}</span>
                {productPickerCollapsed ? '展開' : '收合'}
              </span>
            </div>
          </div>
          <div
            className="qc-section-body"
            style={productPickerCollapsed ? { display: 'none' } : undefined}
          >
            <div className="qc-search-bar">
              {/* 需求 R29（PM 2026-05-21）：因標籤、葷素為複選欄位、可能展開的 chip
                  較多，為避免擠壓第一列的系列/類別/關鍵字檢索寬度，將篩選條件拆為兩列 —
                  第一列為系列、類別、關鍵字檢索三個欄位；第二列放置產品標籤、葷素備註
                  與搜尋/清除按鈕。 */}
              {/* 需求 R107（PM 2026-08-04：「將報價單的欄位排序調整與產品管理相同，
                  並將關鍵字改為『關鍵字檢索』」）：原順序為 關鍵字 → 系列 → 類別，產品管理則是
                  系列 → 類別 → 關鍵字檢索。本輪只搬動 JSX 節點順序並改標籤文字，state / query /
                  篩選行為完全不動（純排列一致性）。 */}
              <div className="qc-search-row">
                {/* 需求 R106 R6（PM 2026-08-04：「報價單管理的產品選取的選擇方式也
                    依樣改成相同的呈現方式，保持一致性與方便使用性」）：系列／類別改用與型錄管理
                    相同的 .multi-select-*（白底 trigger + 已選 chip + ▲/▼ + 單欄 checkbox 清單），
                    取代 R3 沿用的標籤用 .tag-dropdown（多欄 grid，在這三欄並排的窄欄位會溢出）。
                    樣式來源：styles/multiSelectDropdown.css（四頁共用）。
                    Q1=A：三組之間是「任一符合」，多勾系列不會再把類別的結果一起縮小。 */}
                <div className="form-group" ref={seriesDropdownRef}>
                  <label>系列 (可複選)</label>
                  <div className="multi-select-dropdown">
                    <div
                      className="multi-select-trigger"
                      onClick={() => setSeriesDropdownOpen(prev => !prev)}
                    >
                      <div className="selected-tags">
                        {productSeries.length === 0
                          ? <span className="placeholder-text">全部系列</span>
                          : productSeries.map(s => (
                            <span key={s} className="selected-tag">
                              {s}
                              <button className="tag-remove-btn" onClick={e => { e.stopPropagation(); toggleProductSeries(s); }}>×</button>
                            </span>
                          ))
                        }
                      </div>
                      <span className="arrow">{seriesDropdownOpen ? '▲' : '▼'}</span>
                    </div>
                    {seriesDropdownOpen && (
                      <div className="multi-select-menu">
                        {seriesOptions.map(s => (
                          <label key={s} className="multi-select-option">
                            <input
                              type="checkbox"
                              checked={productSeries.includes(s)}
                              onChange={() => toggleProductSeries(s)}
                            />
                            {s}
                          </label>
                        ))}
                        {seriesOptions.length === 0 && (
                          <div className="multi-select-empty">尚無系列</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group" ref={categoryDropdownRef}>
                  <label>類別 (可複選)</label>
                  <div className="multi-select-dropdown">
                    <div
                      className="multi-select-trigger"
                      onClick={() => setCategoryDropdownOpen(prev => !prev)}
                    >
                      <div className="selected-tags">
                        {productCategory.length === 0
                          ? <span className="placeholder-text">全部類別</span>
                          : productCategory.map(c => (
                            <span key={c} className="selected-tag">
                              {c}
                              <button className="tag-remove-btn" onClick={e => { e.stopPropagation(); toggleProductCategory(c); }}>×</button>
                            </span>
                          ))
                        }
                      </div>
                      <span className="arrow">{categoryDropdownOpen ? '▲' : '▼'}</span>
                    </div>
                    {categoryDropdownOpen && (
                      <div className="multi-select-menu">
                        {categoryOptions.map(c => (
                          <label key={c} className="multi-select-option">
                            <input
                              type="checkbox"
                              checked={productCategory.includes(c)}
                              onChange={() => toggleProductCategory(c)}
                            />
                            {c}
                          </label>
                        ))}
                        {categoryOptions.length === 0 && (
                          <div className="multi-select-empty">尚無類別</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group qc-search-keyword">
                  <label>關鍵字檢索</label>
                  <div className="search-input-wrapper">
                    <span className="search-icon">🔍</span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="產品編號、品名或條碼…"
                      value={productKeyword}
                      onChange={e => setProductKeyword(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleProductSearch()}
                    />
                  </div>
                </div>
              </div>
              <div className="qc-search-row">
                <div className="form-group" ref={tagsDropdownRef}>
                <label>產品標籤</label>
                <div className={`tag-dropdown${tagsDropdownOpen ? ' open' : ''}`}>
                  <div
                    className="tag-dropdown-trigger form-control"
                    onClick={() => setTagsDropdownOpen(prev => !prev)}
                    style={{ flexWrap: 'wrap', gap: '4px', height: 'auto', minHeight: '36px' }}
                  >
                    {productTags.length === 0 ? (
                      <span className="tag-dropdown-placeholder">全部（可複選）</span>
                    ) : (
                      productTags.map(tag => (
                        <span key={tag} className="tag" onClick={e => e.stopPropagation()}>
                          {tag}
                          <button className="tag-remove" onClick={e => { e.stopPropagation(); toggleProductTag(tag); }}>×</button>
                        </span>
                      ))
                    )}
                    <span className="tag-dropdown-arrow" style={{ marginLeft: 'auto' }}>▾</span>
                  </div>
                  <div className="tag-dropdown-menu">
                    {tagOptions.map(opt => (
                      <label key={opt._id} className="tag-dropdown-item">
                        <input
                          type="checkbox"
                          checked={productTags.includes(opt.value)}
                          onChange={() => toggleProductTag(opt.value)}
                        />
                        {opt.value}
                      </label>
                    ))}
                    {tagOptions.length === 0 && (
                      <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', padding: 'var(--spacing-sm)' }}>
                        尚無標籤
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* 需求 R59 D5(A)：篩選面板 vegetarianType 已移除；如需搜尋含特定字樣，改用 keyword 全文搜尋。 */}
                {/* 需求 R123（PM 2026-08-19 拍板 Q1=B/Q2=B/Q3=B/Q4=A/Q5=C）：
                    Section 2 第二列新增「建立時間區間」欄，位置在「產品標籤」右側、「搜尋／清除」按鈕左側；
                    Q5=C 交給 .qc-search-row 現有的 flex-wrap 自動決定（窄視窗自動換行）。
                    DOM／class 沿用型錄新品目錄的 .new-product-date-range/.catalog-filter-label/
                    .date-range-inputs/.date-input/.date-range-sep/.date-range-error（AC4）；
                    .date-input 等子元素樣式抽到 styles/dateRangeFilter.css 供三頁共用（無複製貼上）。 */}
                <div className="form-group qc-search-date-range">
                  <div className="new-product-date-range">
                    <label className="catalog-filter-label">建立時間區間（最長 2 個月）</label>
                    <div className="date-range-inputs">
                      <input
                        type="date"
                        className="date-input"
                        value={productCreatedFrom}
                        onChange={e => { setProductCreatedFrom(e.target.value); setProductDateError(''); }}
                      />
                      <span className="date-range-sep">～</span>
                      <input
                        type="date"
                        className="date-input"
                        value={productCreatedTo}
                        onChange={e => { setProductCreatedTo(e.target.value); setProductDateError(''); }}
                      />
                    </div>
                    {productDateError && <div className="date-range-error">{productDateError}</div>}
                  </div>
                </div>
                <div className="qc-search-actions">
                  <button className="qc-btn qc-btn-primary" onClick={handleProductSearch} disabled={searchLoading}>
                    {searchLoading ? '搜尋中…' : '搜尋'}
                  </button>
                  <button className="qc-btn qc-btn-secondary" onClick={handleClearProductSearch}>清除</button>
                </div>
              </div>
            </div>

            {searchResults.length > 0 && (
              <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: 'var(--spacing-sm) 0' }}>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                  已顯示 {searchResults.length} / 共 {searchTotal} 筆
                </span>
                <button
                  className="qc-btn qc-btn-primary qc-btn-sm"
                  onClick={handleAddProducts}
                  disabled={selectedProductIds.size === 0}
                >
                  添加到報價單 ({selectedProductIds.size})
                </button>
              </div>
              <div className="qc-table-wrap">
                <table className="qc-table qc-search-table">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>
                        <input
                          type="checkbox"
                          className="select-checkbox"
                          checked={isAllLoadedSelected}
                          onChange={toggleSelectAll}
                        />
                      </th>
                      <th>產品編號</th>
                      <th>中文品名</th>
                      <th>英文品名</th>
                      <th>規格</th>
                      <th>箱入數</th>
                      <th>成本來源</th>
                      {/* 需求 R136 Q5：列尾的重複提醒欄；沒有重複時整欄空白，版面不受影響。 */}
                      <th className="qc-dup-col" aria-label="重複提醒" />
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map(p => {
                      // 需求 R136（Q5）：事前提醒——此產品已在下方報價品項清單中。
                      const inQuote = isProductInQuote(quotedProductIds, p);
                      return (
                      <tr
                        key={p._id}
                        className={[
                          selectedProductIds.has(p._id) ? 'qc-row-selected' : '',
                          inQuote ? 'qc-row-in-quote' : '',
                        ].filter(Boolean).join(' ')}
                        onClick={() => toggleProductSelect(p._id)}
                      >
                        <td>
                          <input
                            type="checkbox"
                            className="select-checkbox"
                            checked={selectedProductIds.has(p._id)}
                            onChange={() => toggleProductSelect(p._id)}
                            onClick={e => e.stopPropagation()}
                          />
                        </td>
                        <td className="product-code">{p.productCode}</td>
                        <td>{p.productName}</td>
                        <td>{p.englishName}</td>
                        <td>{p.spec}</td>
                        <td>{p.pcsPerBox ?? '—'}</td>
                        <td>
                          <span className="qc-cost-source-tag">{normalizeCostSource(p.costSource || 'factory')}</span>
                        </td>
                        <td className="qc-dup-col">
                          {inQuote && <span className="qc-dup-tag">{DUPLICATE_ITEM_HINT}</span>}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {searchResults.length < searchTotal && (
                <div style={{ textAlign: 'center', margin: 'var(--spacing-sm) 0' }}>
                  <button
                    className="qc-btn qc-btn-secondary"
                    onClick={handleLoadMoreProducts}
                    disabled={searchLoadingMore}
                  >
                    {searchLoadingMore ? '載入中…' : `載入更多（已顯示 ${searchResults.length} / 共 ${searchTotal} 筆）`}
                  </button>
                </div>
              )}
              </>
            )}
            {/* 需求 R106 Q2=A：系列／類別改陣列後，「完全沒下任何條件」的判斷改看長度。 */}
            {searchResults.length === 0 && !searchLoading && productKeyword === '' && productSeries.length === 0 && productCategory.length === 0 && productTags.length === 0 && (
              <div className="qc-search-empty">請輸入搜尋條件後點擊「搜尋」</div>
            )}
          </div>
        </section>

        {/* ========== SECTION 3: 報價品項清單 ========== */}
        <section className="qc-section">
          <div className="qc-section-header">
            <div className="qc-section-header-left">
              <span className="qc-section-number">3</span>
              報價品項清單
              <span className="qc-item-count">（{items.length} 項）</span>
            </div>
            <div className="qc-section-header-right qc-section3-controls">
              <label className="qc-inline-label">全單加成 (%)</label>
              {/*
                需求 R126（PM 2026-08-21）：全單加成需支援小數（例 1.5%）。
                  真因不是「有驗證規則擋小數」，而是受控數字 state 的往返把中間態吃掉——
                  舊寫法 value 綁數字、onChange 立刻 parseFloat 寫回：
                    打「1.」→ parseFloat('1.')=1 → 回填「1」，小數點當場被抹掉，再打 5 就變 15；
                    打「0」 → 0 → 因 `=== 0 ? ''` 規則整欄被清空，接著「.」→ NaN 被丟棄，0.5 完全打不動。
                  修法沿用 需求 R84 單品加價的既有範式：原始字串 buffer + regex 限位 + onBlur 清 buffer。

                PM 逐字拍板：
                  Q3＝「不支援負數」→ regex 不含負號（舊寫法 `v === '-'` 的分支一併移除）。
                       這同時消掉原本「輸入框收得下 -1、但 calcUnitPrice 寫 if (globalMarkupPct > 0) 才生效」
                       的落差；calcUnitPrice 本身零改動（AC7）。
                  Q5＝「使用者打幾位就存幾位，不做四捨五入」→ 這裡只 parseFloat，
                       不做 toFixed／Math.round，送出時 (globalMarkup || 0) 原值進 Quote.globalMarkup(Number)。
                  Q1（位數上限）／Q2（是否兩頁都改）／Q4（0 時顯示）PM 未填答案：
                       Q1 暫用建議值「小數點後 3 位」＝ GLOBAL_MARKUP_INPUT_RE 的 \d{0,3}，
                          要改只需改這條 regex（兩頁各一處）；
                       Q2 暫依 AC2「兩頁同步」實作（本檔＋QuoteEditPage.js）；
                       Q4 暫維持現況「0 顯示空白」（僅在輸入過程用 buffer 保留使用者打的 0 不被清掉）。
                       ——三題若 PM 另有決議，改動點就是上述三處，已在評論標明。
              */}
              <input
                type="text"
                inputMode="decimal"
                className="form-control form-control-sm qc-markup-input"
                value={editingGlobalMarkup != null ? editingGlobalMarkup : (globalMarkup === 0 ? '' : globalMarkup)}
                style={{ width: 80 }}
                onChange={e => {
                  const v = e.target.value;
                  // Q1 暫定：整數不限位、最多小數點後 3 位；Q3：不接受負號。非法輸入整個不接受（欄位不動）。
                  if (v === '' || GLOBAL_MARKUP_INPUT_RE.test(v)) {
                    setEditingGlobalMarkup(v);
                    const n = (v === '' || v === '.') ? 0 : parseFloat(v);
                    applyGlobalMarkup(isNaN(n) ? 0 : n);
                  }
                }}
                onBlur={() => setEditingGlobalMarkup(null)}
              />
              <button className="qc-btn qc-btn-outline qc-btn-sm" onClick={() => setAllLabeling('auto')}>
                一鍵全選貼標
              </button>
              <button className="qc-btn qc-btn-outline qc-btn-sm" onClick={() => setAllLabeling('none')}>
                一鍵全選取消貼標
              </button>
            </div>
          </div>
          <div className="qc-section-body">
            {items.length === 0 ? (
              <div className="qc-items-empty">尚未加入任何產品，請在上方搜尋並新增。</div>
            ) : (
              /* 需求 R137：PM 2026-08-24 拍板「分頁並非合適此頁面的做法，此案先暫停」；
                 2026-08-27 再拍「維持現況，但讓可見品項範圍變大一些／把 section 高度加高」→
                 這裡維持整份 items 一次渲染、由 .qc-items-table-wrap 內層捲動，不加分頁，
                 只在 CSS 把該容器的 max-height 從 55vh 放寬成 max(520px, calc(100vh - 180px))，
                 可見列數由約 4.5 列增為約 6.4 列（以附圖那台 751px 高的視窗計）。
                 下方 items.map 的 idx 就是 items 的絕對索引（updateItem／removeItem／
                 triggerImageUpload 都靠它），切勿改成 items.slice(...).map(...)。
                 算式、量測與風險詳見 QuoteCreatePage.css 的 Items Table 段註解。 */
              <div className="qc-items-table-wrap">
                <table className="qc-table qc-items-table">
                  <thead>
                    <tr>
                      <th style={{ width: 280 }}>產品資訊</th>
                      <th>規格/箱入</th>
                      <th>
                        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.3' }}>
                          <span>價格指數</span>
                          <span>MOQ</span>
                        </div>
                      </th>
                      <th>
                        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.3' }}>
                          <span>單品加價</span>
                          <span>貼標費用</span>
                        </div>
                      </th>
                      <th style={{ width: 120 }}>單價</th>
                      <th style={{ width: 80 }}>數量</th>
                      {/* 需求 R118（Q6）：業務備註停止回寫，表頭說明同步改成「僅套用於本張報價單」。 */}
                      <th title="葷素備註：自動帶入產品詳細頁的「葷素類別」(Product.vegetarianType)，可手動修改，儲存時回寫產品；業務備註：自動帶入產品詳細頁的「業務備註」(Product.salesNotes)，修改後僅套用於本張報價單，不回寫產品。">
                        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.3' }}>
                          <span>葷素備註</span>
                          <span>業務備註</span>
                        </div>
                      </th>
                      <th style={{ width: 80 }}>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => {
                      // 需求 R32：自定成本啟用、值為有限正數，且與目前來源預設不同時，視為「已修改」需顯示警示。
                      // 加入 isFinite 防護避免 Infinity / NaN 觸發誤警示。
                      const sourceCostForRow = getBaseCostFromSource(item._productData, item.costSource);
                      const customCostNumRow = Number(item.customCostValue);
                      const isCustomCostActive =
                        item.customCostEnabled
                        && Number.isFinite(customCostNumRow)
                        && customCostNumRow > 0
                        && customCostNumRow !== Number(sourceCostForRow);
                      // 需求 R149（AC15）：自訂箱成本換算出的包成本與來源成本不同時，同樣亮紅點。
                      // 比較的是**換算後的包成本**而不是使用者填的箱成本——後者與來源成本
                      // （單包）量級不同，直接比會讓每一列都誤報。
                      const boxPackCostRow = packCostFromCustomBoxCost(item.customBoxCostValue, item.pcsPerBox);
                      const isCustomBoxCostActive =
                        item.customBoxCostEnabled
                        && boxPackCostRow != null
                        && boxPackCostRow !== Number(sourceCostForRow);
                      // 需求 R35：價格階改成非預設時，沿用「單價旁」的 price-modified-dot 觸發。
                      // defaultPriceTier 在加入產品 / 客戶變更 時同步維護；缺值時回推當下客戶預設或 1。
                      const rowDefaultTier = item.defaultPriceTier
                        ?? getDefaultPriceTier(selectedCustomer?.level, item.costSource);
                      const isPriceTierChanged = Number(item.priceTier) !== Number(rowDefaultTier);
                      return (
                      <React.Fragment key={idx}>
                      <tr className={item.isManualPrice || item.isCostSourceChanged || isCustomCostActive || isCustomBoxCostActive || isPriceTierChanged ? 'qc-row-modified' : ''}>
                        <td>
                          <div className="product-info-cell">
                            <div
                              className={`product-thumb-wrapper product-image-download-host${canInlineEdit ? ' product-thumb-editable' : ''}`}
                              onClick={canInlineEdit ? () => triggerImageUpload(idx) : undefined}
                              title={canInlineEdit ? '點擊更新產品圖片' : ''}
                            >
                              {item.productImage ? (
                                <img
                                  className="product-thumb"
                                  src={item.productImage}
                                  alt={item.productName}
                                />
                              ) : (
                                <span className="no-image-badge">無圖片</span>
                              )}
                              {canInlineEdit && (
                                <span className="product-thumb-edit-overlay">更換圖片</span>
                              )}
                              {/* 需求 R88：圖片框左下角截圖小圖示（絕對定位、不佔版面）。
                                  compact 版按鈕自帶 stopPropagation，不會觸發外層縮圖的檔案上傳；
                                  不支援 getDisplayMedia 的瀏覽器 ScreenshotCapture 回傳 null，不渲染。 */}
                              {canInlineEdit && (
                                <span
                                  className="product-thumb-screenshot-btn"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <ScreenshotCapture
                                    compact
                                    onUploaded={(url) => syncProductFieldAcrossItems(
                                      typeof item.product === 'object' ? item.product?._id : item.product,
                                      'productImage',
                                      url || ''
                                    )}
                                  />
                                </span>
                              )}
                              {/* 需求 R152（PM 2026-09-01 留言）：報價單頁的產品縮圖
                                  hover 顯示下載小圖示（右下角，與左下角截圖鈕左右對稱）。
                                  ⚠️ 與截圖鈕最大的不同：**不綁 canInlineEdit**。PM 指名的正是
                                  「報價單-詳情頁（已審核通過、不得更改）」—— 那個狀態下 canInlineEdit
                                  為 false，截圖鈕與「更換圖片」遮罩都不渲染，若下載鈕也跟著綁權限，
                                  PM 要的畫面就永遠出不來。下載是唯讀行為，看得到圖就能存下來。
                                  元件自帶 stopPropagation／preventDefault，可編輯狀態下點它也不會
                                  觸發外層 triggerImageUpload 的檔案選擇視窗。 */}
                              <ProductImageDownloadButton src={item.productImage} productName={item.productName} />
                            </div>
                            <div className="product-info-text">
                              {/* 需求 R25：產品資訊改顯示「產品貨編」(productCode)；以 _productData fallback 保證一致性。 */}
                              <span className="product-code">{item.productCode || item._productData?.productCode || ''}</span>
                              <span className="product-name-zh">{item.productName}</span>
                              {canInlineEdit && editingField?.idx === idx && editingField?.field === 'englishName' ? (
                                <input
                                  autoFocus
                                  type="text"
                                  className="form-control form-control-sm qc-inline-edit-input"
                                  defaultValue={item.englishName}
                                  onBlur={(e) => {
                                    const v = e.target.value;
                                    const productId = typeof item.product === 'object' ? item.product?._id : item.product;
                                    syncProductFieldAcrossItems(productId, 'englishName', v);
                                    setEditingField(null);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') { e.target.blur(); }
                                    if (e.key === 'Escape') { setEditingField(null); }
                                  }}
                                />
                              ) : (
                                <span
                                  className={`product-name-en${canInlineEdit ? ' qc-inline-editable' : ''}`}
                                  onClick={canInlineEdit ? () => setEditingField({ idx, field: 'englishName' }) : undefined}
                                  title={canInlineEdit ? '點擊編輯英文品名' : ''}
                                >
                                  {item.englishName || (canInlineEdit ? '（點擊新增英文品名）' : '')}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.5' }}>
                            <span className="qc-spec">{item.spec}</span>
                            {item.pcsPerBox && <span className="qc-pcs">{item.pcsPerBox}入</span>}
                            {/* 需求 R25：國際條碼移至規格/箱入下方。 */}
                            {(item.barcode || item._productData?.barcode) && (
                              <span className="qc-barcode">{item.barcode || item._productData?.barcode}</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <select
                              className="form-control form-control-sm qc-tier-select"
                              value={item.priceTier}
                              onChange={e => updateItem(idx, 'priceTier', parseInt(e.target.value))}
                            >
                              <option value={1}>1階</option>
                              <option value={2}>2階</option>
                              <option value={3}>3階</option>
                              <option value={4}>4階</option>
                            </select>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={item.moq}
                              onChange={e => updateItem(idx, 'moq', e.target.value)}
                            />
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <input
                              type="text"
                              inputMode="decimal"
                              className="form-control form-control-sm"
                              // 需求 R84（PM 2026-07-24 拍板 D1=A/D2/D3）：單品加價需能輸入
                              // 小數，最多至小數點後第 3 位（硬限 D1=A）。改用 _editingItemMarkup 原始字串
                              // buffer 渲染，保留使用者正在輸入的中間態（'0.'、'12.3'），避免受控數字綁定
                              // 在打「.」時被 parseFloat→NaN 或 0→'' 吃掉；僅在合法時同步解析成數字供運算。
                              // D2：欄位保留 3 位輸入/儲存精度，最終單價仍走 calcUnitPrice 既有進位規則不變。
                              value={item._editingItemMarkup != null ? item._editingItemMarkup : (item.itemMarkup === 0 ? '' : item.itemMarkup)}
                              onChange={e => {
                                const v = e.target.value;
                                // D1=A：最多小數點後 3 位（\d{0,3}）、允許負號與中間態；超過 3 位或非法字元不接受。
                                if (v === '' || /^-?\d*\.?\d{0,3}$/.test(v)) {
                                  const n = (v === '' || v === '-' || v === '.' || v === '-.') ? 0 : parseFloat(v);
                                  setItems(prev => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], _editingItemMarkup: v };
                                    return next;
                                  });
                                  updateItem(idx, 'itemMarkup', isNaN(n) ? 0 : n);
                                }
                              }}
                              onBlur={() => setItems(prev => {
                                const next = [...prev];
                                next[idx] = { ...next[idx], _editingItemMarkup: null };
                                return next;
                              })}
                            />
                            <div className="qc-labeling-cell">
                              <select
                                className="form-control form-control-sm"
                                value={item.labelingMode}
                                onChange={e => updateItem(idx, 'labelingMode', e.target.value)}
                              >
                                <option value="none">不貼標</option>
                                <option value="auto">自動計算</option>
                                <option value="manual">手動輸入</option>
                              </select>
                              {item.labelingMode === 'manual' && (
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  className="form-control form-control-sm qc-labeling-manual"
                                  value={item._editingLabelingFee != null ? item._editingLabelingFee : (item.labelingFee === 0 ? '' : item.labelingFee)}
                                  onChange={e => {
                                    const v = e.target.value;
                                    if (v === '' || /^\d*\.?\d*$/.test(v)) {
                                      const n = v === '' ? 0 : parseFloat(v);
                                      setItems(prev => {
                                        const next = [...prev];
                                        const fee = isNaN(n) ? 0 : n;
                                        let updated = { ...next[idx], _editingLabelingFee: v, labelingFee: fee };
                                        if (!updated.isManualPrice) {
                                          const rate = getCurrentRate();
                                          updated.unitPrice = calcUnitPrice(updated, rate, specialFormula, currency, globalMarkup);
                                        }
                                        next[idx] = updated;
                                        return next;
                                      });
                                    }
                                  }}
                                  onBlur={e => {
                                    const v = e.target.value;
                                    const n = v === '' ? 0 : parseFloat(v);
                                    setItems(prev => {
                                      const next = [...prev];
                                      next[idx] = { ...next[idx], _editingLabelingFee: null, labelingFee: isNaN(n) ? 0 : n };
                                      return next;
                                    });
                                  }}
                                />
                              )}
                              {item.labelingMode === 'auto' && (
                                <span className="qc-labeling-fee-hint">{item.labelingFee}</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="qc-price-cell">
                            <input
                              type="text"
                              className={`qc-price-input${item.isManualPrice ? ' qc-manual-price' : ''}`}
                              value={item._editingPrice != null ? item._editingPrice : formatPrice(item.unitPrice, currency)}
                              onChange={e => setItems(prev => {
                                const next = [...prev];
                                next[idx] = { ...next[idx], _editingPrice: e.target.value };
                                return next;
                              })}
                              onBlur={e => {
                                const raw = e.target.value;
                                // 需求 R36：空字串／全空白等同點擊紅點還原 → 取消手動覆寫並回到 calcUnitPrice 結果
                                if (raw.trim() === '') {
                                  handleRestoreAutoPrice(idx);
                                  setItems(prev => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], _editingPrice: null };
                                    return next;
                                  });
                                  return;
                                }
                                const val = parseFloat(raw);
                                if (!isNaN(val) && val !== item.unitPrice) handleManualPrice(idx, val);
                                setItems(prev => {
                                  const next = [...prev];
                                  next[idx] = { ...next[idx], _editingPrice: null };
                                  return next;
                                });
                              }}
                            />
                            {(item.isManualPrice || item.isCostSourceChanged || isCustomCostActive || isCustomBoxCostActive || isPriceTierChanged) && (
                              <span
                                className="price-modified-dot"
                                title={(() => {
                                  // 需求 R35：可能同時觸發多個原因，組合呈現避免遮蓋（含階數變動）。
                                  const reasons = [];
                                  if (item.isManualPrice) reasons.push('單價已手動調整，點擊還原');
                                  // 需求 R149 Q6=A：兩種自訂成本的理由分開寫，看得出是哪一種在生效。
                                  if (isCustomCostActive) reasons.push('已使用自訂包成本換算');
                                  if (isCustomBoxCostActive) reasons.push('已使用自訂箱成本換算');
                                  if (item.isCostSourceChanged) reasons.push('成本來源已變更');
                                  if (isPriceTierChanged) reasons.push(`價格階已調整為非預設（預設為 ${rowDefaultTier} 階）`);
                                  return reasons.join('\n');
                                })()}
                                onClick={() => item.isManualPrice && handleRestoreAutoPrice(idx)}
                              >!</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <input
                            type="text"
                            inputMode="numeric"
                            className="form-control form-control-sm"
                            value={item.quantity === 0 ? '' : item.quantity}
                            onChange={e => {
                              const v = e.target.value;
                              if (v === '') { updateItem(idx, 'quantity', 0); return; }
                              const n = parseInt(v, 10);
                              if (!isNaN(n) && n >= 0) updateItem(idx, 'quantity', n);
                            }}
                          />
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* 需求 R59 D7(A)：葷素備註改純文字自由輸入（推翻 需求 R34「僅下拉」拍板方向）。
                                儲存時的 _origVegetarianType 比對＋回寫 Product.vegetarianType 邏輯保留不變。 */}
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={item.vegetarianNote || ''}
                              placeholder="葷素備註"
                              title="葷素備註（資料來源：產品詳細頁；儲存時若異動會回寫 Product.vegetarianType）"
                              onChange={e => updateItem(idx, 'vegetarianNote', e.target.value)}
                            />
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={item.salesNotes || ''}
                              placeholder="業務備註"
                              readOnly={!canInlineEdit}
                              // 需求 R118（Q6）：停回寫後文案不得再宣稱「儲存時回寫產品」。
                              title={canInlineEdit ? '業務備註（僅套用於本張報價單，不回寫產品）' : '業務備註（唯讀）'}
                              // 需求 R118（Q7=B「取消連動」）：只改當下這一列，
                              // 不再用 syncProductFieldAcrossItems 把同一產品的其他列一起改。
                              onChange={e => {
                                if (!canInlineEdit) return;
                                updateItem(idx, 'salesNotes', e.target.value);
                              }}
                            />
                          </div>
                        </td>
                        <td>
                          <div className="row-actions">
                            <button
                              className={`btn-icon-sm btn-icon-sm-outline${infoPanel === idx ? ' qc-eye-btn-active' : ''}`}
                              title="產品資訊"
                              onClick={() => setInfoPanel(infoPanel === idx ? null : idx)}
                            >
                              👁
                            </button>
                            <button
                              className="btn-icon-sm btn-icon-sm-danger"
                              onClick={() => removeItem(idx)}
                              title="移除此品項"
                            >
                              ×
                            </button>
                          </div>
                        </td>
                      </tr>
                        </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                  {/* 需求 R34（PM 2026-05-22 留言）：
                      取消葷素備註自由輸入功能 → 改為純 <select> 下拉，原本的 datalist 已不再需要，移除以免殘留 DOM。 */}
                </div>
            )}
          </div>
        </section>

        {/* ========== SECTION 4: 交易細節 ========== */}
        <section className="qc-section">
          <div className="qc-section-header">
            <div className="qc-section-header-left">
              <span className="qc-section-number">4</span>
              交易細節與備註
            </div>
          </div>
          <div className="qc-section-body">
            {/* 需求 R143（PM 2026-08-29 拍板 Q2=A）：Section 4 改三欄並排
                ——「交易條件 ／ 備註 ／（建立者＋聯絡人）」。
                交易條件欄除了寬度改變（CSS .footer-grid：2fr 1fr → 1fr 1fr 1fr →
                PM 2026-08-29 回饋 B 再改 3fr 2fr 1fr＝50%/33.3%/16.7%，右欄減半、
                釋出的寬度全部給交易條件一欄，備註維持 33.3%）
                之外，必填、抓取前次交易記錄按鈕、disabled／title 一律維持原狀（AC3）。
                欄高：PM 回饋 A 要求「交易條件（含抓取前次交易紀錄）高度與備註欄相同」，
                由 CSS 兩欄 textarea 各自 flex:1 達成，JSX 不需要改。 */}
            <div className="footer-grid">
              {/* Left column: 交易條件（＝原本的「交易條件備註」，Q3=A 只改標籤名稱） */}
              <div className="notes-left">
                <label>交易條件 <span className="qc-required">*</span></label>
                <textarea
                  className="form-control qc-notes-textarea"
                  value={transactionNotes}
                  onChange={e => setTransactionNotes(e.target.value)}
                  rows={4}
                  placeholder="輸入交易條件，或點擊下方按鈕抓取前次交易記錄..."
                />
                {/* 需求 R143 Q4=A：「抓取前次交易記錄」只服務交易條件，備註欄不得有這顆按鈕。 */}
                <button
                  className="qc-btn qc-btn-outline qc-btn-sm notes-bottom-btn"
                  onClick={fetchLastTransaction}
                  disabled={!selectedCustomer}
                  title={!selectedCustomer ? '請先選擇客戶' : ''}
                >
                  抓取前次交易記錄
                </button>
              </div>
              {/* Middle column: 備註（需求 R143 新增）
                  ⚠️ 本欄內容**不會**出現在匯出的 PDF／Excel（PM 原始需求／AC6-AC7）：
                     quotePdfExport.js／quoteExcelExport.js 只讀 transactionNotes，
                     不要把 internalNotes 接進那兩支檔案。 */}
              <div className="notes-middle">
                <label>備註</label>
                <textarea
                  className="form-control qc-notes-textarea"
                  value={internalNotes}
                  onChange={e => setInternalNotes(e.target.value)}
                  rows={4}
                  placeholder="內部備註（不會顯示在匯出的報價單上）..."
                />
              </div>
              {/* Right column: 建立者 ＋ 聯絡人 */}
              <div>
                {/* 需求 R143（AC9／AC11）：建立者固定排在「聯絡人 (業務)」之上，自動帶入目前
                    登入帳號的姓名。readOnly＋tabIndex=-1＝不可輸入、不可清空、鍵盤也 focus 不到；
                    它也不進 payload、不參與驗證（AC12），存檔後的實際值由後端 create 以
                    req.user._id 寫入（AC13）。 */}
                <div className="form-group">
                  <label>建立者</label>
                  <input
                    type="text"
                    className="form-control qc-readonly-field"
                    value={user?.name || ''}
                    readOnly
                    tabIndex={-1}
                  />
                </div>
                <div className="form-group">
                  <label>聯絡人 (業務) <span className="qc-required">*</span></label>
                  <select
                    className="form-control"
                    value={contactPerson}
                    onChange={e => setContactPerson(e.target.value)}
                  >
                    <option value="">— 請選擇 —</option>
                    {salesUsers.map(u => (
                      <option key={u._id} value={u._id}>{u.name}</option>
                    ))}
                  </select>
                </div>
                {(() => {
                  const selectedSalesUser = salesUsers.find(u => u._id === contactPerson);
                  if (!selectedSalesUser) return null;
                  return (
                    <div className="contact-info-card">
                      <div className="contact-info-row">
                        <span className="contact-info-label">姓名</span>
                        <span className="contact-info-value">{selectedSalesUser.name}</span>
                      </div>
                      {/* 需求 R81（PM 2026-07-22）：聯絡人聯絡欄位改 E-mail，畫面預覽不再顯示電話 */}
                      {selectedSalesUser.email && (
                        <div className="contact-info-row">
                          <span className="contact-info-label">E-mail</span>
                          <span className="contact-info-value">{selectedSalesUser.email}</span>
                        </div>
                      )}
                      {selectedSalesUser.lineId && (
                        <div className="contact-info-row">
                          <span className="contact-info-label">通訊方式</span>
                          <span className="contact-info-value">{selectedSalesUser.lineId}</span>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </section>

        {/* Bottom action bar */}
        <div className="qc-bottom-bar">
          <div className="action-bar-left">
            最後編輯: 儲存後可查看
          </div>
          <div className="action-bar-right">
            <button className="qc-btn qc-btn-outline" onClick={() => setShowCancelModal(true)}>
              取消
            </button>
            <button className="qc-btn qc-btn-secondary" onClick={() => handleSave(false)} disabled={saving}>
              {saving ? '儲存中…' : '暫存草稿'}
            </button>
            <button className="qc-btn qc-btn-primary" onClick={() => handleSave(true)} disabled={saving}>
              {saving ? '送出中…' : '送出審核'}
            </button>
            {/* 需求 R119（Q1=B）：建立頁（尚未存檔＝草稿）一律不給列印，故此處不再有「列印」按鈕。
                存檔並送審後，於詳情頁 /quotes/:id 或編輯頁列印。 */}
          </div>
        </div>
      </div>

      {/* Product Info Drawer */}
      {(() => {
        const drawerItem = infoPanel != null ? items[infoPanel] : null;
        const drawerPd = drawerItem?._productData;
        const drawerCostEntry = drawerItem?.costSource === 'factory'
          ? drawerPd?.factoryCost
          : drawerPd?.distributorCosts?.[parseInt((drawerItem?.costSource || '').split('_')[1], 10)];
        const volumeMethodLabel = (m) => m === 'auto' ? '自動計算' : m === 'supplier' ? '廠商提供' : m === 'estimate' ? '預估' : '—';
        return (
          <>
            <div
              className={`qc-product-drawer-overlay${infoPanel != null ? ' active' : ''}`}
              onClick={() => setInfoPanel(null)}
            />
            <div className={`qc-product-drawer${infoPanel != null ? ' active' : ''}`}>
              {drawerItem && (
                <>
                  <div className="qc-product-drawer-header">
                    <h3>產品資訊 - {drawerItem.productName}</h3>
                    <button className="qc-product-drawer-close" onClick={() => setInfoPanel(null)}>&times;</button>
                  </div>
                  <div className="qc-product-drawer-body">
                    {/* Section 1: 標準成本資料 */}
                    <div className="qc-info-section">
                      <div className="qc-info-section-title">標準成本資料 (報價來源)</div>
                      <table className="qc-info-cost-table">
                        <thead>
                          <tr>
                            <th>選取</th>
                            <th>成本來源</th>
                            <th>標準成本</th>
                            <th>MOQ</th>
                          </tr>
                        </thead>
                        {['factory', ...(drawerPd?.distributorCosts || []).map((_, i) => `distributor_${i}`)].map(src => {
                          const entry = src === 'factory' ? drawerPd?.factoryCost : drawerPd?.distributorCosts?.[parseInt(src.split('_')[1], 10)];
                          // 需求 R46：抽屜「標準成本」與 getBaseCostFromSource 同源。
                          // 需求 R132：同源的定義已改為 resolveEntryBaseCost（箱價÷箱入數的未進位原值），
                          // 所以這裡顯示的就是**實際代入單價算式的那個數字**（PM 案例會看到 59.85，
                          // 產品管理頁則依 需求 R70 維持顯示 59.9）。刻意讓兩邊不同：報價單使用者
                          // 要能像 PM 那樣自己驗算 59.85 + 1.045 = 60.895 → 進位 60.9 對得起來。
                          const displayUnitPrice = formatBaseCost(resolveEntryBaseCost(entry, drawerPd?.pcsPerBox));
                          const label = src === 'factory' ? '工廠' : `經銷${parseInt(src.split('_')[1]) + 1}`;
                          const isActive = drawerItem.costSource === src;
                          const activeCls = isActive ? 'qc-info-cost-active' : '';
                          return (
                            <tbody key={src} className={`qc-info-cost-group ${isActive ? 'qc-info-cost-group-active' : ''}`}>
                              <tr
                                className={`qc-info-cost-main-row qc-info-cost-row-clickable ${activeCls}`}
                                onClick={() => handleCostSourceChange(infoPanel, src)}
                              >
                                <td>
                                  <input
                                    type="radio"
                                    name={`cost-source-drawer`}
                                    checked={drawerItem.costSource === src}
                                    onChange={() => handleCostSourceChange(infoPanel, src)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </td>
                                <td>{label}</td>
                                <td className="qc-info-cost-price">${displayUnitPrice ?? '—'}</td>
                                <td>{entry?.moq || '—'}</td>
                              </tr>
                              <tr
                                className={`qc-info-cost-notes-row qc-info-cost-row-clickable ${activeCls}`}
                                onClick={() => handleCostSourceChange(infoPanel, src)}
                              >
                                <td colSpan={4} className="qc-info-cost-notes-cell">
                                  <span className="qc-info-cost-notes-label">備註：</span>
                                  <span className="qc-info-cost-notes-text">{entry?.notes || '（無）'}</span>
                                </td>
                              </tr>
                            </tbody>
                          );
                        })}
                      </table>
                    </div>

                    {/* 需求 R32 / 需求 R149: 自訂成本換算（僅影響本筆報價單 item，不回寫商品主檔） */}
                    {(() => {
                      const drawerSourceCost = getBaseCostFromSource(drawerPd, drawerItem.costSource);
                      const customValNum = Number(drawerItem.customCostValue);
                      const customValValid = Number.isFinite(customValNum) && customValNum > 0;
                      const isCustomCostActiveDrawer =
                        drawerItem.customCostEnabled
                        && customValValid
                        && customValNum !== Number(drawerSourceCost);
                      const hint = !drawerItem.customCostEnabled
                        ? '啟用後以自填包成本取代來源成本進行單價計算'
                        : (customValValid
                            ? (isCustomCostActiveDrawer
                                ? `目前以自訂包成本 ${customValNum} 取代來源成本 ${drawerSourceCost ?? '—'} 進行計算`
                                : '目前自訂包成本與來源成本相同，計算結果不變')
                            : '目前仍使用原成本來源計算');
                      const editing = drawerItem._editingCustomCost;
                      const inputValue = editing != null
                        ? editing
                        : (customValValid ? customValNum : '');

                      // ── 需求 R149：右欄「自訂箱成本」
                      // Q2=A 換算式、Q3=A 不進位，兩者都由 packCostFromCustomBoxCost 一手包辦
                      // （本頁不自己除，AC8）。這裡算出來的 boxPackCost 只用於 AC16 的提示文字，
                      // 真正進單價的那一次換算發生在 getEffectiveBaseCost 內。
                      const boxCostAllowed = canUseCustomBoxCost(drawerItem);
                      const boxValNum = Number(drawerItem.customBoxCostValue);
                      const boxValValid = Number.isFinite(boxValNum) && boxValNum > 0;
                      const boxPackCost = packCostFromCustomBoxCost(drawerItem.customBoxCostValue, drawerItem.pcsPerBox);
                      const isCustomBoxCostActiveDrawer =
                        drawerItem.customBoxCostEnabled
                        && boxPackCost != null
                        && boxPackCost !== Number(drawerSourceCost);
                      const boxHint = !boxCostAllowed
                        ? CUSTOM_BOX_COST_NO_PCS_HINT
                        : (!drawerItem.customBoxCostEnabled
                            ? '啟用後以自填箱成本 ÷ 箱入數換算出包成本，再取代來源成本計算'
                            : (boxValValid
                                ? (isCustomBoxCostActiveDrawer
                                    ? `目前以自訂箱成本 ${boxValNum} ÷ 箱入數 ${drawerItem.pcsPerBox} ＝ 包成本 ${boxPackCost} 取代來源成本 ${drawerSourceCost ?? '—'} 進行計算`
                                    : '目前換算出的包成本與來源成本相同，計算結果不變')
                                : '目前仍使用原成本來源計算'));
                      const editingBox = drawerItem._editingCustomBoxCost;
                      const boxInputValue = editingBox != null
                        ? editingBox
                        : (boxValValid ? boxValNum : '');
                      return (
                        <div className="qc-info-section qc-info-custom-cost-section">
                          {/* 需求 R149 Q5=A：畫面用字統一為「訂」。 */}
                          <div className="qc-info-section-title">自訂成本換算</div>
                          {/* 需求 R149 需求三：一列二欄、左右排列（Q10=A 窄螢幕塌成單欄，見 CSS）。 */}
                          <div className="qc-info-detail-block qc-info-custom-cost-grid">
                            <div className={`qc-info-custom-cost-col${isCustomCostActiveDrawer ? ' qc-info-custom-cost-col-active' : ''}`}>
                              <label className="qc-info-custom-cost-toggle">
                                <input
                                  type="checkbox"
                                  checked={!!drawerItem.customCostEnabled}
                                  onChange={(e) => handleCustomCostToggle(infoPanel, e.target.checked)}
                                />
                                <span>使用自訂包成本換算</span>
                              </label>
                              <div className="qc-info-custom-cost-input-row">
                                <input
                                  type="number"
                                  inputMode="decimal"
                                  step="any"
                                  min="0"
                                  className="form-control form-control-sm qc-info-custom-cost-input"
                                  disabled={!drawerItem.customCostEnabled}
                                  value={inputValue}
                                  onChange={(e) => handleCustomCostChange(infoPanel, e.target.value)}
                                  onBlur={() => setItems(prev => {
                                    const next = [...prev];
                                    next[infoPanel] = { ...next[infoPanel], _editingCustomCost: undefined };
                                    return next;
                                  })}
                                />
                                {/*
                                  需求 R105（PM 2026-08-04 拍板 Q1=A / Q2=A / Q3=A / Q4=A）：
                                  此處原本有一顆幣別標籤 <span>，內容是 drawerPd?.currency || currency
                                  || 'TWD'，已刪除；Q2=A 連同它專用的 CSS 規則一起從
                                  QuoteCreatePage.css 移除（該規則在 span 刪掉後即為零引用死碼）。
                                  原因：Product schema 沒有 currency 欄位，第一段 fallback 永遠取不到值，
                                  於是一律顯示報價單本身的幣別 state（USD／HKD…）。但此欄輸入的是「取代
                                  來源標準成本的成本基準」，與報價單輸出幣別無關（換算 ÷匯率 發生在階數
                                  計算之後，見 需求 R82 D3／需求 R83），標上幣別會誤導使用者以為要填
                                  該幣別金額。Q3=A：輸入框維持 max-width:160px 靠左，版面不動。
                                  需求 R149 沿用同一條決議：新增的箱成本輸入框旁同樣不標幣別。
                                */}
                              </div>
                              <div className="qc-info-custom-cost-hint">{hint}</div>
                            </div>
                            <div className={`qc-info-custom-cost-col${isCustomBoxCostActiveDrawer ? ' qc-info-custom-cost-col-active' : ''}${boxCostAllowed ? '' : ' qc-info-custom-cost-col-disabled'}`}>
                              <label className="qc-info-custom-cost-toggle">
                                <input
                                  type="checkbox"
                                  checked={!!drawerItem.customBoxCostEnabled}
                                  /* Q4=A：箱入數缺漏 → 換算不出包成本，整欄停用。 */
                                  disabled={!boxCostAllowed}
                                  onChange={(e) => handleCustomBoxCostToggle(infoPanel, e.target.checked)}
                                />
                                <span>使用自訂箱成本換算</span>
                              </label>
                              <div className="qc-info-custom-box-cost-input-row">
                                <input
                                  type="number"
                                  inputMode="decimal"
                                  step="any"
                                  min="0"
                                  className="form-control form-control-sm qc-info-custom-cost-input"
                                  disabled={!boxCostAllowed || !drawerItem.customBoxCostEnabled}
                                  value={boxInputValue}
                                  onChange={(e) => handleCustomBoxCostChange(infoPanel, e.target.value)}
                                  onBlur={() => setItems(prev => {
                                    const next = [...prev];
                                    next[infoPanel] = { ...next[infoPanel], _editingCustomBoxCost: undefined };
                                    return next;
                                  })}
                                />
                              </div>
                              <div className="qc-info-custom-cost-hint">{boxHint}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    <hr className="qc-info-divider" />

                    {/* Section 2: 材積來源 */}
                    <div className="qc-info-section">
                      <div className="qc-info-section-title">
                        材積來源
                        <span className={`qc-info-volume-light ${drawerPd?.volumeMethod === 'estimate' ? 'qc-info-light-red' : 'qc-info-light-yellow'}`} />
                      </div>
                      <div className="qc-info-detail-block">
                        <div className="qc-info-detail-row">
                          <span className="qc-info-detail-label">資料來源：</span>
                          <span className="qc-info-detail-value">{volumeMethodLabel(drawerPd?.volumeMethod)}</span>
                        </div>
                        <div className="qc-info-detail-row">
                          <span className="qc-info-detail-label">才數 / CBM：</span>
                          <span className="qc-info-detail-value">
                            {drawerPd?.volumeCuft != null ? `${parseFloat(drawerPd.volumeCuft).toFixed(2)} 才` : '—'} / {drawerPd?.volumeCbm != null ? `${parseFloat(drawerPd.volumeCbm).toFixed(3)} CBM` : '—'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <hr className="qc-info-divider" />

                    {/* Section 3: 產品備註 */}
                    <div className="qc-info-section">
                      <div className="qc-info-section-title">產品備註</div>
                      <div className="qc-info-notes-block">
                        {(drawerCostEntry?.notes || drawerPd?.productNotes) || '（無備註）'}
                      </div>
                    </div>

                    <hr className="qc-info-divider" />

                    {/* Section 4: 出貨地區 */}
                    <div className="qc-info-section">
                      <div className="qc-info-section-title">出貨地區</div>
                      <div className="qc-info-shipping-block">
                        {drawerCostEntry?.shippableCountries && (
                          <div className="qc-info-shipping-row">
                            <span className="qc-info-shipping-ok">✓ 可出貨國家</span>
                            <div className="qc-info-shipping-countries">{drawerCostEntry.shippableCountries}</div>
                          </div>
                        )}
                        {drawerCostEntry?.nonShippableCountries && (
                          <div className="qc-info-shipping-row">
                            <span className="qc-info-shipping-no">✕ 不可出貨國家</span>
                            <div className="qc-info-shipping-countries">{drawerCostEntry.nonShippableCountries}</div>
                          </div>
                        )}
                        {!drawerCostEntry?.shippableCountries && !drawerCostEntry?.nonShippableCountries && (
                          <div className="qc-info-shipping-empty">（未設定出貨地區資料）</div>
                        )}
                      </div>
                    </div>

                    <hr className="qc-info-divider" />

                    {/* Section 5: 查看產品詳細資料 */}
                    <div className="qc-info-section qc-info-section-center">
                      <button
                        className="qc-btn qc-btn-detail"
                        onClick={() => window.open(`/products/${drawerItem.product}`, '_blank')}
                      >
                        查看產品詳細資料
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        );
      })()}

      {/* Hidden file input for product image upload */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={handleImageFileChange}
      />

      {/* Toast notifications */}
      {toasts.length > 0 && (
        <div className="qc-toast-container">
          {toasts.map(t => (
            <div key={t.id} className={`qc-toast qc-toast-${t.type}`}>
              {t.message}
            </div>
          ))}
        </div>
      )}

      {/* Cancel confirmation modal */}
      {showCancelModal && (
        <div className="qc-modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="qc-modal" onClick={e => e.stopPropagation()}>
            <div className="qc-modal-title">確認取消</div>
            <div className="qc-modal-body">確定要取消新增報價單嗎？未儲存的內容將會遺失。</div>
            <div className="qc-modal-actions">
              <button className="qc-btn qc-btn-secondary" onClick={() => setShowCancelModal(false)}>
                繼續編輯
              </button>
              <button className="qc-btn qc-btn-danger" onClick={() => { setShowCancelModal(false); navigate('/quotes'); }}>
                確定取消
              </button>
            </div>
          </div>
        </div>
      )}

      <CompanyModal
        show={companyModalShow}
        onClose={() => setCompanyModalShow(false)}
        onSaved={() => fetchCompanies()}
        company={companyModalEdit}
        isEdit={!!companyModalEdit}
      />
    </div>
  );
};

export default QuoteCreatePage;
